import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import LockedDocumentsBanner from 'features/documentAccess/LockedDocumentsBanner'
import ReactDOM from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { isNil, pathOr, propOr } from 'ramda'
import { isNotNil, isNotNilOrEmpty } from 'utils/ramda'

// 🔹 Material UI
import {
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Button,
  Chip,
  CircularProgress,
  Skeleton,
} from '@mui/material'

// 🔹 Icons
import GridViewIcon from '@mui/icons-material/GridView'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SegmentOutlinedIcon from '@mui/icons-material/SegmentOutlined'
import ListIcon from '@mui/icons-material/List'
import SortIcon from '@mui/icons-material/Sort'
import EditIcon from '@mui/icons-material/Edit'
import ShareIcon from '@mui/icons-material/Share'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import FolderIcon from '@mui/icons-material/Folder'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import DescriptionIcon from '@mui/icons-material/Description'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

// 🔹 Redux selectors & actions
import {
  getSelectedBp,
  localSearchFileResults,
  localSearchTruncated,
  selectIsSearchLoading,
} from 'features/bpDetails/ducks/selectors'
import { selectFileCategories, selectFolders } from 'ducks/dictionaries/selectors'
import { getFileTypes, selectBpFilesList } from 'ducks/files/selectors'
import { selectSelectedUnitFiles } from 'ducks/units/selectors'
import { selectIsFetchingFiles } from 'ducks/loaderSelectors'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import {
  getBpConnectedUsersRoutine,
  searchLocalFilesRoutine,
} from '../../ducks/actions'

// 🔹 Components & Modals
import File from 'features/bpDetails/Components/File'
import ShareBulkFilesModal from '../ShareBulkFilesModal'
import UnsharedBulkFilesModal from '../UnshareBulkFilesModal'
import UnshareAllForEmailModal from '../UnshareAllForEmailModal'
import BulkEditFileModal from '../EditBulkFilesModal'
import ReclassifyFileModal from '../ReclassifyFileModal'
import { FilePermissionsModal } from '../File'
import Modal from 'components/atoms/Modal'
import { toast } from 'react-toastify'
import { bulkDeleteFiles, bulkDownloadFiles } from 'services/fileService'
import { bulkRemoveFilesRoutine, loadAllFilesRoutine, getBpFilesCountsRoutine } from 'ducks/files/actions'
import { fetchBpRoutine } from 'features/bpDetails/ducks/actions'
import ViewedStatusModal from './ViewedStatusModal'

// 🔹 Utils & Services
import { getFileTypeName } from 'utils/bpData'
import { canEditBp } from 'utils/user'
import CookieService from 'services/CookieService'
import APIService from 'services/APIService'
import FolderPermissionsIndicator from './FolderPermissionsIndicator'
import {
  getAutoScrollSpeed,
  getIntersectingItemIds,
  getSelectionRange,
  haveSameSelection,
  hasExceededDragThreshold,
  isTextEditingTarget,
  mergeUniqueSelection
} from 'utils/fileMultiSelection'

// 🔹 Virtualization
import { FixedSizeGrid, FixedSizeList } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'
import AutoSizer from 'react-virtualized-auto-sizer'
import { IDENTITY_FOLDER_ID } from './IdentityDocumentsFolder'

const NO_EXTRA_FILES = []

const COMPACT_TOOLBAR_BREAKPOINT = 1100
const GROUP_RENDER_CAP = 60
const LIST_ROW_HEIGHT = 48

const FileCategories = ({
  setRecentFilesVisible,
  unit,
  canEditUnit,
  showOnlyMyFiles = false,
  setIsSearching,
  isSearching = false,
  selectedFolder = null,
  selectedCategory = null,
  searchCriteria = null,
  onFolderSelect = null, // Callback to select folder in tree
  onCategorySelect = null, // Callback to select category in tree
  // New props for merged functionality
  showTabs = false,
  showTreeNavigation = false,
  layoutMode = 'single', // 'single' | 'side-by-side'
  onOpenEditModal,
  totalBpResults,
  setSearchCriteria,
  selectedViewableBy = [], // Filter files by specific user's access
  files: filesProp, // Files from parent (search results or all files)
  searchMode = 'name', // Search mode: 'name' or 'content'
  onOpenMobileDrawer = null, // Callback to open mobile drawer
  isLoadingFiles = false, // Show skeleton loading state while files are being fetched
  context = 'bp',
  hasMore = false,
  loadingMore = false,
  onLoadMore = () => {},
  total,
  extraFiles = NO_EXTRA_FILES
}) => {
  const [currentFolder, setCurrentFolder] = useState(null)
  const [currentCategory, setCurrentCategory] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [isLegacyView, setIsLegacyView] = useState(false)

  // State for merged functionality from OtherFiles
  const [expandedFolders, setExpandedFolders] = useState(new Set())
  const [hasAutoSelected, setHasAutoSelected] = useState(false)

  // Use selectedFolder/selectedCategory from props if provided, otherwise use internal state
  const effectiveFolder = selectedFolder || currentFolder

  const fileTypes = useSelector(getFileTypes)
  const fileCategories = useSelector(selectFileCategories)
  const folders = useSelector(selectFolders)
  const bpFiles = useSelector(selectBpFilesList)
  const unitFiles = useSelector(selectSelectedUnitFiles)
  const searchResults = useSelector(localSearchFileResults)
  const isSearchTruncated = useSelector(localSearchTruncated)
  const isSearchLoadingRedux = useSelector(selectIsSearchLoading)
  const bp = useSelector(getSelectedBp)
  const currentUser = useSelector(getCurrentUser)
  const isUnit = isNotNilOrEmpty(unit)

  // Set default view mode to 'list' on mobile, 'grid' on desktop
  const getInitialViewMode = () => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      return 'list'
    }
    return 'grid'
  }

const [viewMode, setViewMode] = useState(getInitialViewMode)
  const [groupByCategory, setGroupByCategory] = useState(false)
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set())
  const [fullyShownGroups, setFullyShownGroups] = useState(() => new Set())
  const [isCompactToolbarViewport, setIsCompactToolbarViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 920 : false
  )
  const [sortBy, setSortBy] = useState('newest')
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState(null)
  const [sortBottomSheetOpen, setSortBottomSheetOpen] = useState(false)
  const [folderUserCounts, setFolderUserCounts] = useState({})

  // Auto-switch to list view on mobile, grid on desktop when window resizes
  useEffect(() => {
    const handleResize = () => {
      setIsCompactToolbarViewport(window.innerWidth <= 920)

      if (window.innerWidth <= 768 && viewMode === 'grid') {
        setViewMode('list')
      } else if (window.innerWidth > 768 && viewMode === 'list') {
        // For now, we keep list view on desktop if user switched to it
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [viewMode])

  // Fetch folder user counts
  useEffect(() => {
    const isOwner =
      bp?.owners?.some(owner => owner.id === currentUser?.id) ||
      bp?.owner?.id === currentUser?.id

    if (bp?.id && isOwner) {
      const api = APIService()
      api
        .get(`/building-passport/${bp.id}/folders/user-counts`)
        .then(resp => {
          setFolderUserCounts(resp?.data?.data || {})
        })
        .catch(err => {
          console.error('Failed to fetch folder user counts:', err)
        })
    }
  }, [bp?.id, currentUser])

  // Use files from props (includes search results), or fall back to Redux selectors
  const files = useMemo(() => {
    const base = filesProp || (isUnit ? unitFiles : bpFiles)
    if (extraFiles.length === 0) return base
    const seen = new Set((base || []).map(f => f.id))
    return [...(base || []), ...extraFiles.filter(f => !seen.has(f.id))]
  }, [filesProp, bpFiles, unitFiles, isUnit, extraFiles])

  useEffect(() => {
    if (files?.length) {
      const uncategorised = files.filter(f => !f.fileType?.id);
    }
  }, [files]);

  // Auto-switch to relevance sorting when content search results arrive
  useEffect(() => {
    const hasSearchScores = files?.some(f => f.searchScore !== undefined)
    if (hasSearchScores && sortBy === 'newest') {
      setSortBy('relevance')
    }
  }, [files])

  // Reset to 'newest' sorting when switching from content to name mode
  useEffect(() => {
    if (searchMode === 'name' && sortBy === 'relevance') {
      setSortBy('newest')
    }
  }, [searchMode])

  const isFetchingFiles = useSelector(selectIsFetchingFiles)

  const isEstateAggregateContext = context === 'estate-aggregate' || context === 'portfolio-aggregate'
  const searchScopeLabel = context === 'portfolio-aggregate' ? 'portfolio' : context === 'estate-aggregate' ? 'estate' : 'Building Passport'
  const canEdit = isEstateAggregateContext ? false : (isUnit ? canEditUnit : canEditBp(currentUser, bp))
  const [selectedFiles, setSelectedFiles] = useState([])
  const selectionAnchorRef = useRef(null)
  const [isMobileSelectionMode, setIsMobileSelectionMode] = useState(false)
  const [bulkAnchorEl, setBulkAnchorEl] = useState(null)
  const isBulkMenuOpen = Boolean(bulkAnchorEl)
  const dispatch = useDispatch()
  const [bulkShareModalOpen, setBulkShareModalOpen] = useState(false)
  const [bulkUnsharedModalOpen, setBulkUnsharedModalOpen] = useState(false)
  const [unshareAllModalOpen, setUnshareAllModalOpen] = useState(false)
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false)
  const [bulkPermissionsModalOpen, setBulkPermissionsModalOpen] = useState(false)
  const [bulkRemoveModalOpen, setBulkRemoveModalOpen] = useState(false)
  const [bulkBusyAction, setBulkBusyAction] = useState(null)

  // Central reclassify modal state to prevent unmounting issues
  const [reclassifyModalOpen, setReclassifyModalOpen] = useState(false)
  const [reclassifyFile, setReclassifyFile] = useState(null)
  const [permissionsFile, setPermissionsFile] = useState(null)
  const [viewedStatusFile, setViewedStatusFile] = useState(null)

  // Simple drag selection state
  const [isDragSelecting, setIsDragSelecting] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [dragEnd, setDragEnd] = useState({ x: 0, y: 0 })
  const [dragScrollOffset, setDragScrollOffset] = useState({ x: 0, y: 0 })
  const [dragViewport, setDragViewport] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0
  })
  const containerRef = useRef(null)
  const virtualizedScrollRef = useRef(null)
  const isMouseDownRef = useRef(false)
  const isDragSelectingRef = useRef(false)
  const dragStartRef = useRef({ x: 0, y: 0 })
  const selectedDuringDragRef = useRef(new Set())
  const dragSelectionBaseRef = useRef([])
  const isAdditiveDragRef = useRef(false)
  const dragCardRectsRef = useRef(new Map())
  const lastDragPointerRef = useRef({ x: 0, y: 0 })
  const autoScrollFrameRef = useRef(null)
  const suppressNextContentClickRef = useRef(false)

  // Simple drag selection handlers
  const handleMouseDown = useCallback(e => {
    // Don't start drag on interactive elements
    if (
      e.target.closest('button') ||
      e.target.closest('input') ||
      e.target.closest('a') ||
      e.target.closest('[role="dialog"]')
    ) {
      return
    }

    if (e.button !== 0) return // Only left mouse button

    const scrollContainer = virtualizedScrollRef.current
    const wrapper = containerRef.current
    if (!scrollContainer || !wrapper) return

    const rect = scrollContainer.getBoundingClientRect()
    const wrapperRect = wrapper.getBoundingClientRect()
    if (
      e.clientX < rect.left ||
      e.clientX > rect.right ||
      e.clientY < rect.top ||
      e.clientY > rect.bottom
    ) return

    const x = e.clientX - rect.left + scrollContainer.scrollLeft
    const y = e.clientY - rect.top + scrollContainer.scrollTop

    const start = { x, y }
    const isAdditiveDrag = e.ctrlKey || e.metaKey
    const selectionBase = isAdditiveDrag ? selectedFiles : []
    dragStartRef.current = start
    dragSelectionBaseRef.current = selectionBase
    isAdditiveDragRef.current = isAdditiveDrag
    selectedDuringDragRef.current = new Set(selectionBase)
    dragCardRectsRef.current = new Map()
    lastDragPointerRef.current = { x: e.clientX, y: e.clientY }
    isMouseDownRef.current = true
    setDragStart(start)
    setDragEnd({ x, y })
    setDragScrollOffset({
      x: scrollContainer.scrollLeft,
      y: scrollContainer.scrollTop
    })
    setDragViewport({
      left: rect.left - wrapperRect.left,
      top: rect.top - wrapperRect.top,
      width: rect.width,
      height: rect.height
    })
    window.__dragMovement = 0
    e.preventDefault()
  }, [selectedFiles])

  const updateDragSelection = useCallback((clientX, clientY) => {
    const scrollContainer = virtualizedScrollRef.current
    const wrapper = containerRef.current
    if (!scrollContainer || !wrapper) return

    const rect = scrollContainer.getBoundingClientRect()
    const wrapperRect = wrapper.getBoundingClientRect()

    const x = clientX - rect.left + scrollContainer.scrollLeft
    const y = clientY - rect.top + scrollContainer.scrollTop

    setDragEnd({ x, y })
    setDragScrollOffset({
      x: scrollContainer.scrollLeft,
      y: scrollContainer.scrollTop
    })
    setDragViewport({
      left: rect.left - wrapperRect.left,
      top: rect.top - wrapperRect.top,
      width: rect.width,
      height: rect.height
    })

    // Remember content-relative card positions as they are mounted. The grid is
    // virtualized, so cards that scroll out of view must remain measurable for
    // the current marquee without remaining selected forever.
    const fileCards = containerRef.current.querySelectorAll('.file-card')
    const visitedFileIds = new Set()

    fileCards.forEach(card => {
      const fileId = card.dataset.fileId
      if (!fileId || visitedFileIds.has(fileId)) return
      visitedFileIds.add(fileId)

      const cardRect = card.getBoundingClientRect()
      const cardLeft = cardRect.left - rect.left + scrollContainer.scrollLeft
      const cardTop = cardRect.top - rect.top + scrollContainer.scrollTop
      dragCardRectsRef.current.set(fileId, {
        left: cardLeft,
        top: cardTop,
        right: cardLeft + cardRect.width,
        bottom: cardTop + cardRect.height
      })
    })

    const intersectingFileIds = getIntersectingItemIds(
      dragCardRectsRef.current,
      {
        left: dragStartRef.current.x,
        top: dragStartRef.current.y,
        right: x,
        bottom: y
      }
    )
    const nextSelectedIds = mergeUniqueSelection(
      dragSelectionBaseRef.current,
      intersectingFileIds
    )
    const selectedIds = Array.from(selectedDuringDragRef.current)

    if (!haveSameSelection(selectedIds, nextSelectedIds)) {
      selectedDuringDragRef.current = new Set(nextSelectedIds)
      setSelectedFiles(nextSelectedIds)
    }
  }, [])

  const startAutoScroll = useCallback(() => {
    if (autoScrollFrameRef.current) return

    const scrollStep = () => {
      autoScrollFrameRef.current = null
      if (!isDragSelectingRef.current) return

      const { x, y } = lastDragPointerRef.current
      const scrollContainer = virtualizedScrollRef.current
      if (!scrollContainer) return

      const rect = scrollContainer.getBoundingClientRect()
      const scrollSpeed = getAutoScrollSpeed(
        y - rect.top,
        rect.height,
        120,
        48
      )

      if (scrollSpeed !== 0) {
        const previousScrollTop = scrollContainer.scrollTop
        scrollContainer.scrollBy(0, scrollSpeed)

        if (scrollContainer.scrollTop !== previousScrollTop) {
          updateDragSelection(x, y)
        }

        autoScrollFrameRef.current = window.requestAnimationFrame(scrollStep)
      }
    }

    autoScrollFrameRef.current = window.requestAnimationFrame(scrollStep)
  }, [updateDragSelection])

  const handleMouseMove = useCallback(
    e => {
      if (!isMouseDownRef.current && !isDragSelectingRef.current) return

      const scrollContainer = virtualizedScrollRef.current
      if (!scrollContainer) return

      lastDragPointerRef.current = { x: e.clientX, y: e.clientY }
      const rect = scrollContainer.getBoundingClientRect()
      const current = {
        x: e.clientX - rect.left + scrollContainer.scrollLeft,
        y: e.clientY - rect.top + scrollContainer.scrollTop
      }
      const movement = Math.max(
        Math.abs(current.x - dragStartRef.current.x),
        Math.abs(current.y - dragStartRef.current.y)
      )
      window.__dragMovement = movement

      if (
        !isDragSelectingRef.current &&
        hasExceededDragThreshold(dragStartRef.current, current)
      ) {
        isDragSelectingRef.current = true
        selectedDuringDragRef.current = new Set(dragSelectionBaseRef.current)
        dragCardRectsRef.current = new Map()
        if (!isAdditiveDragRef.current) setSelectedFiles([])
        setIsDragSelecting(true)
        window.__isDragSelecting = true
      }

      if (!isDragSelectingRef.current) return

      e.preventDefault()
      e.stopPropagation()
      updateDragSelection(e.clientX, e.clientY)
      startAutoScroll()
    },
    [startAutoScroll, updateDragSelection]
  )

  const handleVirtualizedScroll = useCallback(() => {
    if (!isDragSelectingRef.current) return

    const { x, y } = lastDragPointerRef.current
    updateDragSelection(x, y)
  }, [updateDragSelection])

  const handleMouseUp = useCallback(e => {
    const completedDragSelection = isDragSelectingRef.current
    if (completedDragSelection) {
      e.preventDefault()
      e.stopPropagation()
      suppressNextContentClickRef.current = true
      window.setTimeout(() => {
        suppressNextContentClickRef.current = false
      }, 0)
    }
    isMouseDownRef.current = false
    isDragSelectingRef.current = false
    dragSelectionBaseRef.current = []
    isAdditiveDragRef.current = false
    dragCardRectsRef.current = new Map()
    if (autoScrollFrameRef.current) {
      window.cancelAnimationFrame(autoScrollFrameRef.current)
      autoScrollFrameRef.current = null
    }
    setIsDragSelecting(false)
    window.__isDragSelecting = false
    window.__dragMovement = 0
  }, [])

  const handleContentClick = useCallback(e => {
    if (suppressNextContentClickRef.current) return
    if (
      e.target.closest('.file-card') ||
      e.target.closest('button') ||
      e.target.closest('input') ||
      e.target.closest('a') ||
      e.target.closest('[role="button"]') ||
      e.target.closest('[role="dialog"]') ||
      e.target.closest('.MuiModal-root')
    ) return

    setSelectedFiles([])
    selectionAnchorRef.current = null
    setIsMobileSelectionMode(false)
  }, [])

  // Event listeners for drag selection
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      if (autoScrollFrameRef.current) {
        window.cancelAnimationFrame(autoScrollFrameRef.current)
        autoScrollFrameRef.current = null
      }
      isMouseDownRef.current = false
      isDragSelectingRef.current = false
      dragSelectionBaseRef.current = []
      isAdditiveDragRef.current = false
      dragCardRectsRef.current = new Map()
      window.__isDragSelecting = false
      window.__dragMovement = 0
    }
  }, [handleMouseMove, handleMouseUp])

  const handleUncategorisedFilter = useCallback(() => {
    if (setIsSearching) {
      setIsSearching(true)
    }

    dispatch(
      searchLocalFilesRoutine({
        query: '',
        exactMatch: true,
        uploaderIds: [],
        fileTypeIds: ['null'], // 'null' filters for uncategorised files
        bpId: bp.id,
        onComplete: () => {}
      })
    )
  }, [dispatch, bp.id, setIsSearching])

  const handleClearFilter = useCallback(() => {
    if (setIsSearching) {
      setIsSearching(false)
    }
  }, [setIsSearching])

  // Re-run the current search with the unlimited flag so the backend skips the LIMIT
  const handleLoadAllResults = useCallback(() => {
    if (!searchCriteria?.isActive) return
    dispatch(
      searchLocalFilesRoutine({
        query: searchCriteria.query || '',
        uploaderIds: searchCriteria.uploaderIds || [],
        fileTypeIds: searchCriteria.fileTypeIds || [],
        fileTagIds: searchCriteria.tagIds || [],
        searchMode: searchMode,
        bpId: bp.id,
        unlimited: true,
      })
    )
  }, [dispatch, bp?.id, searchCriteria, searchMode])

  // Central reclassify modal handlers
  const handleOpenReclassifyModal = useCallback(file => {
    setReclassifyFile(file)
    setReclassifyModalOpen(true)
  }, [])

  const handleCloseReclassifyModal = useCallback(() => {
    setReclassifyModalOpen(false)
    setReclassifyFile(null)
  }, [])

  const handleOpenPermissionsModal = useCallback(file => {
    setPermissionsFile(file)
  }, [])

  const handleOpenViewedStatusModal = useCallback(file => {
    setViewedStatusFile(file)
  }, [])

  useEffect(() => {
    if (bp?.id && bulkShareModalOpen) {
      dispatch(getBpConnectedUsersRoutine(bp.id))
    }
  }, [bulkShareModalOpen, bp?.id])

  const folderTabs = useMemo(() => {
    if (!folders) return []
    return folders
  }, [folders])

  const foldersWithAll = useMemo(() => {
    const allFilesFolder = {
      id: 'all_files',
      name: 'All Files',
      isVirtual: true,
      files: files || [],
      categories: []
    }
    return [allFilesFolder, ...(folders || [])]
  }, [folders, files])


  const legacyCategories = useMemo(() => {
    if (!fileCategories || !files) return []

    // Categories without folder assignment
    const categoriesWithoutFolder = fileCategories
      .filter(category => !category.folderId)
      .map(category => {
        return {
          ...category,
          files: files.filter(
            file =>
              pathOr('', ['fileType', 'fileCategoryId'], file) === category.id
          )
        }
      })

    // Uncategorized files
    const uncategorizedFiles = files.filter(file =>
      isNil(pathOr(null, ['fileType', 'fileCategoryId'], file))
    )

    const result = []

    // Add uncategorised folder first if it has files
    if (uncategorizedFiles.length > 0) {
      result.push({
        id: 'uncategorised',
        name: 'Uncategorised',
        files: uncategorizedFiles,
        isCustom: true
      })
    }

    // Then add all other categories
    result.push(...categoriesWithoutFolder)

    return result
  }, [fileCategories, files])

  // Group categories by folder
  const categoriesByFolder = useMemo(() => {
    if (!fileCategories || !folders) return {}

    const result = {}

    fileCategories.forEach(category => {
      if (category.folderId) {
        if (!result[category.folderId]) {
          result[category.folderId] = []
        }
        result[category.folderId].push(category)
      }
    })

    return result
  }, [fileCategories, folders])

  const filterMyFiles = useCallback(
    filesList => {
      if (!showOnlyMyFiles || !filesList) return filesList
      return filesList.filter(file => file.uploader?.id === currentUser.id)
    },
    [showOnlyMyFiles, currentUser.id]
  )

  // Frontend filtering function that applies search criteria
  const filterFilesByCriteria = useCallback(
    (filesList, currentFolder = null, currentCategory = null) => {
      if (!searchCriteria?.isActive || !filesList) return filesList

      // Compute scope locally — never mutate searchCriteria, never fall back to uncategorised
      const limitToCurrentFolder = !!(currentFolder || currentCategory)

      console.log('Filtering files:', {
        searchCriteria,
        inputFiles: filesList.length,
        isActive: searchCriteria.isActive,
        hasQuery: searchCriteria.query?.length >= 3,
        hasFileTypes: searchCriteria.fileTypeIds?.length > 0,
        hasUploaders: searchCriteria.uploaderIds?.length > 0,
        hasTags: searchCriteria.tagIds?.length > 0,
        currentFolder: currentFolder?.id,
        currentCategory: currentCategory?.id,
        limitToCurrentFolder
      })

      let filteredFiles = [...filesList]

      if (limitToCurrentFolder) {
        if (currentCategory) {
          if (currentCategory.id === 'uncategorised') {
            filteredFiles = filteredFiles.filter(file => {
              const type = file.fileType
              return !type || !type.id || type.id === null
            })
          } else {
            filteredFiles = filteredFiles.filter(
              file => pathOr('', ['fileType', 'fileCategoryId'], file) === currentCategory.id
            )
          }
        } else if (currentFolder) {
          if (currentFolder.id === 'all_files') {
            console.log('🟢 [Filter] All Files - skip folder limitation')
          }
          else if (currentFolder.id === 'uncategorised') {
            filteredFiles = filteredFiles.filter(file => {
              const type = file.fileType
              return !type || !type.id || type.id === null
            })
          } else {
            const folderCategoryIds = fileCategories
              ?.filter(cat => cat.folderId === currentFolder.id)
              .map(cat => cat.id) || []

            filteredFiles = filteredFiles.filter(file => {
              const categoryId = file.fileType?.fileCategoryId
              return folderCategoryIds.includes(categoryId)
            })
          }
        }
      }

      if (currentFolder?.id === 'all_files') {
        filteredFiles = filesList.filter(file => {
          const name = file.displayName?.toLowerCase() || ''
          const query = searchCriteria.query?.toLowerCase() || ''

          const matchesQuery = !query || name.includes(query)
          const matchesUploader =
            !searchCriteria.uploaderIds?.length ||
            searchCriteria.uploaderIds.includes(file.uploader?.id)

          const fileTypeId = file.fileType?.id
          const fileTypeIdForComparison = fileTypeId ?? 'null'
          const matchesType =
            !searchCriteria.fileTypeIds?.length ||
            searchCriteria.fileTypeIds.includes(fileTypeIdForComparison)

          const matchesTags =
            !searchCriteria.tagIds?.length ||
            file.tags?.some(tag => searchCriteria.tagIds.includes(tag.id))

          return matchesQuery && matchesUploader && matchesType && matchesTags
        })
      }

      filteredFiles = filteredFiles.filter((file, index) => {
        if (index === 0) {
          console.log('Sample file structure:', {
            displayName: file.displayName,
            fileType: file.fileType,
            uploader: file.uploader,
            tags: file.tags
          })
        }

        // Query filter (search in display name, description, content)
        if (searchCriteria.query && searchCriteria.query.trim().length > 0) {
          const normalize = s =>
            (s || '')
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .replace(/\s+/g, ' ')
              .trim()

          const query = normalize(searchCriteria.query)

          // 🔥 Include toate câmpurile relevante pentru căutare:
          const allText = normalize(
            [
              file.displayName,
              file.description,
              file.searchableContent,
              file.fileType?.name,
              file.fileType?.fileCategory?.name,
              file.uploader?.name,
              (file.taggings || [])
                .map(t => t.tag?.name || t.tag?.label || t.name)
                .join(' '),
            ].join(' ')
          )

          if (searchCriteria.query && searchCriteria.query.length >= 3) {
            const query = searchCriteria.query.toLowerCase()
            const allText = `${file.displayName || ''} ${file.description || ''} ${file.searchableContent || ''}`.toLowerCase()

            if (searchCriteria.exactMatch) {
              if (!allText.includes(query)) return false
            } else {
              const qWords = query.split(' ').filter(Boolean)
              if (!qWords.every(w => allText.includes(w))) return false
            }
          }
        }

        // Uploader filter
        if (
          searchCriteria.uploaderIds &&
          searchCriteria.uploaderIds.length > 0
        ) {
          if (!searchCriteria.uploaderIds.includes(file.uploader?.id)) {
            return false
          }
        }

        // File type filter
        if (
          searchCriteria.fileTypeIds &&
          searchCriteria.fileTypeIds.length > 0
        ) {
          const fileTypeId = file.fileType?.id
          const fileTypeIdForComparison = fileTypeId ?? 'null' // Convert null/undefined to 'null' string

          if (index === 0) {
            console.log('File type filtering:', {
              searchFileTypeIds: searchCriteria.fileTypeIds,
              fileTypeId: fileTypeId,
              fileTypeIdForComparison: fileTypeIdForComparison,
              matches: searchCriteria.fileTypeIds.includes(
                fileTypeIdForComparison
              )
            })
          }

          if (!searchCriteria.fileTypeIds.includes(fileTypeIdForComparison)) {
            return false
          }
        }

        // Tag filter - AND logic (all selected tags must be present)
        if (searchCriteria.tagIds && searchCriteria.tagIds.length > 0) {
          // Files have 'taggings' property, not 'tags'
          const fileTaggings = file.taggings || []
          // Extract tag IDs from taggings - taggings might have tagId or tag.id structure
          const fileTagIds = fileTaggings
            .map(tagging => tagging.tagId || tagging.tag?.id || tagging.id)
            .filter(Boolean)

          // Check if ALL selected tags are present in the file (AND logic)
          const hasAllTags = searchCriteria.tagIds.every(tagId =>
            fileTagIds.includes(tagId)
          )
          if (!hasAllTags) {
            return false
          }
        }

        return true
      })

      return filteredFiles
    },
    [searchCriteria, fileCategories]
  )

  const effectiveFiles = useMemo(() => {
    if (!files) return []

    const isSearchActive = searchCriteria?.isActive

    // Helper to apply folder/category narrowing
    const applyScope = (list) => {
      let filtered = list
      if (selectedFolder) {
        if (selectedFolder.id === 'uncategorised') {
          filtered = filtered.filter(f => !f.fileType?.fileCategoryId)
        } else if (selectedFolder.id !== 'all_files' && selectedFolder.id !== IDENTITY_FOLDER_ID) {
          const folderCategoryIds = fileCategories
            .filter(c => c.folderId === selectedFolder.id)
            .map(c => c.id)
          filtered = filtered.filter(f => {
            const categoryId = f.fileType?.fileCategoryId
            return folderCategoryIds.includes(categoryId)
          })
        }
      }
      if (selectedCategory) {
        filtered = filtered.filter(
          f => f.fileType?.fileCategoryId === selectedCategory.id
        )
      }
      return filtered
    }

    let filtered = applyScope(files)

    // When search is active and the folder/category filter would yield zero,
    // fall back to showing all search results across folders.
    if (isSearchActive && filtered.length === 0 && files.length > 0) {
      filtered = files
    }

    // When search is active, Bedrock KB already did text filtering - don't duplicate it
    if (!isSearchActive && searchCriteria?.query?.length > 1) {
      const q = searchCriteria.query.toLowerCase()
      filtered = filtered.filter(f =>
        `${f.displayName || ''} ${f.description || ''} ${f.searchableContent || ''}`
          .toLowerCase()
          .includes(q)
      )
    }

    return filtered
  }, [files, selectedFolder, selectedCategory, searchCriteria, fileCategories])

  const currentViewCategories = useMemo(() => {
    if (isLegacyView) {
      return legacyCategories
    }

    // If we're searching, show Bedrock KB search results
    if (searchCriteria?.isActive) {
      // Use effectiveFiles which are already filtered by Bedrock KB
      const searchResultsCategory = {
        id: 'search-results',
        name: selectedFolder?.name === 'All Files'
          ? 'All Files (Search Results)'
          : 'Search Results',
        files: effectiveFiles, // Use files that went through effectiveFiles memo
        isSearchResults: true
      }

      return [searchResultsCategory]
    }

    // If no folder is selected (tree navigation) and no category is selected, show all folders as categories
    if (
      !effectiveFolder &&
      !selectedFolder &&
      !(selectedCategory || currentCategory)
    ) {
      const allFiles = filterMyFiles(files)
      const filteredAll = filterFilesByCriteria(allFiles)

      return [
        {
          id: 'all-files',
          name: 'All Files',
          files: filteredAll,
          isCustom: true
        }
      ]
    }

    // If a folder is selected, show categories from that folder
    if (!effectiveFolder || !files) return []

    const folderId = effectiveFolder.id

    // Handle special uncategorised folder
    if (folderId === 'uncategorised') {
      const uncategorisedFiles = files.filter(file => !file?.fileType?.id)
      const myFilesFiltered = filterMyFiles(uncategorisedFiles)
      const searchFiltered = filterFilesByCriteria(myFilesFiltered)

      return [
        {
          id: 'uncategorised',
          name: 'All Files',
          files: searchFiltered
        }
      ]
    }

    if (folderId === IDENTITY_FOLDER_ID) {
      return filterMyFiles(files)
    }

    if (folderId === 'all_files') {
      const baseFiles =
        searchCriteria?.isActive && searchResults?.length
          ? searchResults
          : files

      const myFilesFiltered = filterMyFiles(baseFiles)
      const searchFiltered = filterFilesByCriteria(myFilesFiltered, effectiveFolder)

      return searchFiltered
    }

    const categoriesInFolder = categoriesByFolder[folderId] || []

    return categoriesInFolder.map(category => {
      const categoryFiles = files.filter(
        file => pathOr('', ['fileType', 'fileCategoryId'], file) === category.id
      )

      // Apply both filters: "My Files" and search criteria
      const myFilesFiltered = filterMyFiles(categoryFiles)
      const searchFiltered = filterFilesByCriteria(myFilesFiltered, effectiveFolder, category)

      return {
        ...category,
        files: searchFiltered
      }
    })
  }, [
    isLegacyView,
    effectiveFolder,
    selectedFolder,
    selectedCategory,
    currentCategory,
    files,
    folders,
    categoriesByFolder,
    legacyCategories,
    filterMyFiles,
    filterFilesByCriteria,
    searchCriteria,
    effectiveFiles
  ])

  // When selectedCategory is provided from props, ensure it has files populated
  const effectiveCategory = useMemo(() => {
    // If search is active, always return search results category from currentViewCategories
    if (searchCriteria?.isActive) {
      const searchResultsCategory = currentViewCategories.find(
        cat => cat.id === 'search-results'
      )
      return searchResultsCategory
    }

    const category = selectedCategory || currentCategory
    if (!category || !files) return category

    // If the category doesn't have files populated, add them
    if (category && !category.files) {
      const categoryFiles = files.filter(
        file => pathOr('', ['fileType', 'fileCategoryId'], file) === category.id
      )
      return {
        ...category,
        files: categoryFiles
      }
    }

    return category
  }, [
    selectedCategory,
    currentCategory,
    files,
    currentViewCategories,
    searchCriteria?.isActive
  ])

  const totalResultsCount = useMemo(() => {
    if (!files) return 0;

    const normalizedQuery = (searchCriteria?.query || '').trim().toLowerCase();

    return files.filter(f => {
      const allText = [
        f.displayName,
        f.description,
        f.searchableContent,
        f.fileType?.name,
        f.fileType?.fileCategory?.name,
        f.uploader?.name,
        ...(f.taggings || []).map(t => t.tag?.name || t.tag?.label || t.name),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesQuery =
        !normalizedQuery || allText.includes(normalizedQuery);

      const fileTypeId = f.fileType?.id ?? 'null';
      const matchesType =
        !searchCriteria?.fileTypeIds?.length ||
        searchCriteria.fileTypeIds.includes(fileTypeId);

      const matchesUploader =
        !searchCriteria?.uploaderIds?.length ||
        searchCriteria.uploaderIds.includes(f.uploader?.id);

      const matchesTags =
        !searchCriteria?.tagIds?.length ||
        (f.taggings || []).some(t =>
          searchCriteria.tagIds.includes(t.tagId || t.tag?.id)
        );

      return matchesQuery && matchesType && matchesUploader && matchesTags;
    }).length;
  }, [files, searchCriteria]);


  useEffect(() => {
    if (
      searchCriteria?.isActive &&
      selectedCategory?.id === 'uncategorised' &&
      files?.length > 0
    ) {
      const matching = files.filter(f => !f.fileType?.id).filter(f => {
        const q = searchCriteria.query?.toLowerCase() || '';
        const all = `${f.displayName || ''} ${f.description || ''} ${f.searchableContent || ''}`.toLowerCase();
        return q && all.includes(q);
      });

      if (matching.length > 0) {
        setCurrentCategory({
          id: 'uncategorised',
          name: 'Uncategorised (Search Results)',
          files: matching,
          isSearchResults: true,
        });
      }
    }
  }, [searchCriteria, selectedCategory?.id, files]);


  const hasSelectedCategory = isNotNil(effectiveCategory)
  const hasCategoriesAvailable = currentViewCategories.length > 0
  const hasSelectedFolder = isNotNil(effectiveFolder)
  const isAllFilesFolder = effectiveFolder?.id === 'all_files' || effectiveFolder?.id === IDENTITY_FOLDER_ID

  // Search is now handled by StreamlinedFilesSearch in UnifiedFiles
  // Results are passed down as the 'files' prop
  // No need to call the API again here

  // Auto-select search results when searching and clear folder/category selection
  useEffect(() => {
    if (searchCriteria?.isActive) {
      // Clear folder and category selection when searching (search is global)
      setCurrentFolder(null)

      setCurrentCategory({
        id: 'search-results',
        name: 'Search Results',
        files: searchResults,
        isSearchResults: true
      })
    } else {
      // Clear search results when search is no longer active
      if (currentCategory?.id === 'search-results') {
        setCurrentCategory(null)
      }
    }
  }, [searchCriteria?.isActive, searchResults])

  // Initialize with the first folder if available (only if no selectedFolder prop is provided)
  useEffect(() => {
    if (
      folderTabs.length > 0 &&
      !isLegacyView &&
      !currentFolder &&
      !selectedFolder &&
      !selectedCategory
    ) {
      // Only auto-select first folder if it has <= 500 files
      // This prevents timeouts when loading folders with massive file counts
      const firstFolder = folderTabs[0]
      const fileCount = firstFolder?.fileCount || firstFolder?.file_count || 0

      if (fileCount <= 500) {
        setCurrentFolder(firstFolder)
        setActiveTab(0)
      }
      // If first folder has > 500 files, don't auto-select - let user manually click
    }
  }, [folderTabs, isLegacyView, selectedFolder, selectedCategory])

  // Clear internal folder selection when tree navigation is used
  useEffect(() => {
    if (selectedFolder || selectedCategory) {
      setCurrentFolder(null)
      setCurrentCategory(null)
      setActiveTab(0)
    }
  }, [selectedFolder, selectedCategory])

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    setCurrentFolder(folderTabs[newValue])
    setCurrentCategory(null)
  }

  const handleCategoryChange = category => {
    setCurrentCategory(category)
  }

  // Return to category list
  const handleBackToCategories = () => {
    setCurrentCategory(null)
  }

  const colorPalette = [
    '#0c132b',
    '#bbeaf0',
    '#36becd',
    '#ffc111',
    '#cd2000',
    '#ffaf7f'
  ]

  const GRID_ITEM_WIDTH = 260
  const GRID_ITEM_HEIGHT = 470
  const GRID_ITEM_HEIGHT_MOBILE = 320

  const [searchParams] = useSearchParams()
  const deepLinkFileId = searchParams.get('file')
  const deepLinkFiredForRef = useRef(null)
  const gridLayoutRef = useRef({ columnCount: 1, itemHeight: GRID_ITEM_HEIGHT })

  const sortFiles = filesToSort => {
    if (!filesToSort) return []

    // Check if files have search scores - if so, default to relevance sorting
    const hasSearchScores = filesToSort.some(f => f.searchScore !== undefined)

    switch (sortBy) {
      case 'relevance':
        // Sort by search score (highest first)
        return [...filesToSort].sort((a, b) => {
          const scoreA = a.searchScore || 0
          const scoreB = b.searchScore || 0
          return scoreB - scoreA
        })
      case 'newest':
        return [...filesToSort].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        )
      case 'oldest':
        return [...filesToSort].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        )
      case 'name-asc':
        return [...filesToSort].sort((a, b) =>
          a.displayName.localeCompare(b.displayName)
        )
      case 'name-desc':
        return [...filesToSort].sort((a, b) =>
          b.displayName.localeCompare(a.displayName)
        )
      case 'type':
        return [...filesToSort].sort((a, b) => {
          const typeA = getFileTypeName(
            pathOr('', ['fileType', 'id'], a),
            fileTypes,
            a.isPrivate
          )
          const typeB = getFileTypeName(
            pathOr('', ['fileType', 'id'], b),
            fileTypes,
            b.isPrivate
          )
          return typeA.localeCompare(typeB)
        })
      case 'expiry-asc':
        return [...filesToSort].sort((a, b) => {
          if (!a.expirationDate && !b.expirationDate) return 0
          if (!a.expirationDate) return 1
          if (!b.expirationDate) return -1
          return new Date(a.expirationDate) - new Date(b.expirationDate)
        })
      case 'expiry-desc':
        return [...filesToSort].sort((a, b) => {
          if (!a.expirationDate && !b.expirationDate) return 0
          if (!a.expirationDate) return 1
          if (!b.expirationDate) return -1
          return new Date(b.expirationDate) - new Date(a.expirationDate)
        })
      default:
        // If files have search scores and no explicit sort is set, use relevance
        if (hasSearchScores && sortBy === 'newest') {
          return [...filesToSort].sort((a, b) => {
            const scoreA = a.searchScore || 0
            const scoreB = b.searchScore || 0
            return scoreB - scoreA
          })
        }
        return filesToSort
    }
  }

  const sortedCategoryFiles = useMemo(() => {
    // If a specific category is selected, show only its files
    if (effectiveCategory) {
      return sortFiles(propOr([], 'files', effectiveCategory))
    }

    if (effectiveFolder?.id === IDENTITY_FOLDER_ID && !searchCriteria?.isActive) {
      return sortFiles(files)
    }

    if (effectiveFolder?.id === 'all_files') {
      const baseFiles = searchCriteria?.isActive
        ? (searchResults?.length > 0 ? searchResults : files)
        : files

      return sortFiles(baseFiles)
    }

    // If no specific category is selected but categories are available, show all files from all categories
    if (hasCategoriesAvailable) {
      const allCategoryFiles = currentViewCategories.reduce((acc, category) => {
        const categoryFiles = propOr([], 'files', category)
        return [...acc, ...categoryFiles]
      }, [])
      return sortFiles(allCategoryFiles)
    }

    return []
  }, [effectiveCategory, effectiveFolder, files, searchCriteria?.isActive, sortBy, hasCategoriesAvailable, currentViewCategories, searchResults])

  const selectableFileIds = useMemo(
    () => sortedCategoryFiles.filter(f => f.canBeAccessed).map(f => f.id),
    [sortedCategoryFiles]
  )

  const selectedBulkBuildingPassports = useMemo(() => {
    if (!isEstateAggregateContext) return []
    const map = new Map()
    sortedCategoryFiles.forEach(file => {
      if (!selectedFiles.includes(file.id)) return
      const fileBp = file.buildingPassport
      if (fileBp?.id) map.set(fileBp.id, fileBp)
    })
    return Array.from(map.values())
  }, [isEstateAggregateContext, sortedCategoryFiles, selectedFiles])

  const bulkTargetBp = selectedBulkBuildingPassports.length === 1
    ? selectedBulkBuildingPassports[0]
    : null

  const bulkCanEdit = isEstateAggregateContext
    ? Boolean(bulkTargetBp?.canEdit)
    : canEdit

  const bulkBlockedHint = isEstateAggregateContext && selectedFiles.length > 0 && !bulkCanEdit
    ? (selectedBulkBuildingPassports.length === 1
      ? 'You do not have permission to edit this building, so these actions are not available for this selection.'
      : 'These actions apply to one building at a time. Select documents from a single building to use them.')
    : null

  const bulkBp = (isEstateAggregateContext ? bulkTargetBp : null) ?? bp
  // Download, permissions and remove only work on building files (not unit files),
  // and in estate/portfolio views only when the selection is from a single building.
  const canBulkDownload = !isUnit && Boolean(bulkBp?.id) && (!isEstateAggregateContext || Boolean(bulkTargetBp))
  const canBulkManageFiles = !isUnit && bulkCanEdit

  const selectedFileObjects = useMemo(
    () => sortedCategoryFiles.filter(f => selectedFiles.includes(f.id)),
    [sortedCategoryFiles, selectedFiles]
  )

  const refreshAfterBulkChange = useCallback(() => {
    if (isEstateAggregateContext || !bulkBp?.id) return
    dispatch(fetchBpRoutine({ id: bulkBp.id }))
    dispatch(loadAllFilesRoutine({ bpId: bulkBp.id }))
    dispatch(getBpFilesCountsRoutine({ id: bulkBp.id }))
  }, [isEstateAggregateContext, bulkBp?.id, dispatch])

  const handleBulkDownload = async () => {
    if (!canBulkDownload || bulkBusyAction) return
    const count = selectedFiles.length
    setBulkBusyAction('download')
    const toastId = toast.info(`Preparing ${count} file${count === 1 ? '' : 's'} for download…`, { autoClose: false })
    try {
      const response = await bulkDownloadFiles(bulkBp.id, selectedFiles)
      const url = URL.createObjectURL(response.data)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `${(bulkBp.name || 'documents').replace(/[^A-Za-z0-9_-]+/g, '-')}-documents.zip`
      anchor.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (error) {
      toast.error('Could not download the selected files. Please try again.')
    } finally {
      toast.dismiss(toastId)
      setBulkBusyAction(null)
    }
  }

  const handleBulkRemove = async () => {
    if (!canBulkManageFiles || bulkBusyAction) return
    setBulkBusyAction('remove')
    try {
      const { data } = await bulkDeleteFiles(selectedFiles)
      const deleted = data?.deleted || []
      const skipped = data?.skipped || []
      if (deleted.length > 0) {
        dispatch(bulkRemoveFilesRoutine.success(deleted))
        toast.success(`${deleted.length} file${deleted.length === 1 ? ' has' : 's have'} been deleted`)
      }
      if (skipped.length > 0) {
        const fromSharePoint = skipped.filter(item => item.reason === 'sharepoint').length
        toast.warn(
          fromSharePoint === skipped.length
            ? `${skipped.length} SharePoint file${skipped.length === 1 ? ' was' : 's were'} not deleted — remove ${skipped.length === 1 ? 'it' : 'them'} in SharePoint.`
            : `${skipped.length} file${skipped.length === 1 ? ' was' : 's were'} not deleted because you don't have permission to remove ${skipped.length === 1 ? 'it' : 'them'}.`
        )
      }
      setSelectedFiles(current => current.filter(id => !deleted.includes(id)))
      setBulkRemoveModalOpen(false)
      refreshAfterBulkChange()
    } catch (error) {
      toast.error('Could not delete the selected files. Please try again.')
    } finally {
      setBulkBusyAction(null)
    }
  }

  const getCategoryColor = useCallback((fileCategoryId) => {
    if (fileCategoryId && fileCategories && folders) {
      const fileCategory = fileCategories.find(cat => cat.id === fileCategoryId)
      if (fileCategory?.folderId) {
        const folderIndex = folders.findIndex(f => f.id === fileCategory.folderId)
        if (folderIndex !== -1) return colorPalette[folderIndex % colorPalette.length]
      }
    }
    return null
  }, [fileCategories, folders])

  const groupedFileSections = useMemo(() => {
    const groups = new Map()
    const order = []
    sortedCategoryFiles.forEach(file => {
      const catId = file?.fileType?.fileCategoryId || 'uncategorised'
      const catName = file?.fileType?.fileCategory?.name
        || fileCategories.find(c => c.id === catId)?.name
        || 'Other documents'
      if (!groups.has(catId)) {
        groups.set(catId, { id: catId, name: catName, color: getCategoryColor(catId), files: [] })
        order.push(catId)
      }
      groups.get(catId).files.push(file)
    })
    return order.map(id => groups.get(id))
  }, [sortedCategoryFiles, fileCategories, getCategoryColor])

  const toggleGroup = useCallback((id) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }, [])

  useEffect(() => {
    if (!deepLinkFileId || deepLinkFiredForRef.current === deepLinkFileId) return undefined

    const idx = sortedCategoryFiles.findIndex(f => String(f.id) === String(deepLinkFileId))
    if (idx === -1) return undefined

    deepLinkFiredForRef.current = deepLinkFileId

    const isGroupedListView = groupByCategory && viewMode === 'list'

    if (isGroupedListView) {
      const targetFile = sortedCategoryFiles[idx]
      const targetCategoryId = targetFile?.fileType?.fileCategoryId || 'uncategorised'

      if (collapsedGroups.has(targetCategoryId)) {
        setCollapsedGroups(prev => {
          const next = new Set(prev)
          next.delete(targetCategoryId)
          return next
        })
      }

      const section = groupedFileSections.find(s => s.id === targetCategoryId)
      const posInGroup = section?.files?.findIndex(f => String(f.id) === String(deepLinkFileId)) ?? -1
      if (posInGroup >= GROUP_RENDER_CAP) {
        setFullyShownGroups(prev => new Set(prev).add(targetCategoryId))
      }
    }

    let attempts = 0
    let timeoutId = null

    const attemptHighlight = () => {
      attempts += 1
      const container = containerRef.current
      const matches = container
        ? Array.from(container.querySelectorAll('[data-file-id]')).filter(
            node => node.dataset.fileId === String(deepLinkFileId)
          )
        : []
      const el = matches[matches.length - 1]

      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.style.transition = 'background-color 0.3s ease, box-shadow 0.3s ease'
        el.style.backgroundColor = '#e6f7f9'
        el.style.boxShadow = '0 0 0 3px rgba(51, 190, 204, 0.18)'
        timeoutId = setTimeout(() => {
          el.style.backgroundColor = ''
          el.style.boxShadow = ''
          el.style.transition = ''
        }, 2000)
        return
      }

      const scrollEl = virtualizedScrollRef.current
      if (scrollEl && !isGroupedListView) {
        const { columnCount, itemHeight } = gridLayoutRef.current
        const rowHeight = viewMode === 'grid' ? itemHeight : LIST_ROW_HEIGHT
        const rowIndex = viewMode === 'grid' ? Math.floor(idx / Math.max(1, columnCount)) : idx
        const targetTop = Math.max(0, rowIndex * rowHeight - scrollEl.clientHeight / 2 + rowHeight / 2)
        scrollEl.scrollTop = targetTop
      }

      if (attempts < 8) {
        timeoutId = setTimeout(attemptHighlight, 300)
      }
    }

    timeoutId = setTimeout(attemptHighlight, 150)
    return () => { if (timeoutId) clearTimeout(timeoutId) }
  }, [deepLinkFileId, sortedCategoryFiles, groupByCategory, collapsedGroups, groupedFileSections, viewMode])

  const allSelectableSelected = useMemo(() => {
    if (selectableFileIds.length === 0) return false
    return selectableFileIds.every(id => selectedFiles.includes(id))
  }, [selectableFileIds, selectedFiles])

  const toggleFileSelection = useCallback(
    (fileId, isChecked, event) => {
      const file = sortedCategoryFiles.find(f => f.id === fileId)
      if (!file?.canBeAccessed) return

      if (event?.shiftKey && selectionAnchorRef.current) {
        const orderedIds = sortedCategoryFiles
          .filter(item => item.canBeAccessed)
          .map(item => item.id)
        const rangeIds = getSelectionRange(
          orderedIds,
          selectionAnchorRef.current,
          fileId
        )

        if (rangeIds.length > 0) {
          setSelectedFiles(rangeIds)
          return
        }
      }

      selectionAnchorRef.current = fileId
      setSelectedFiles(prev => {
        const newSelected = isChecked ? [...prev, fileId] : prev.filter(id => id !== fileId)
        if (newSelected.length === 0) {
          setIsMobileSelectionMode(false)
        }
        return newSelected
      })
    },
    [sortedCategoryFiles]
  )

  useEffect(() => {
    const handleSelectAllShortcut = event => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'a') return

      if (isTextEditingTarget(event.target)) return

      event.preventDefault()
      setSelectedFiles(selectableFileIds)
      selectionAnchorRef.current = selectableFileIds[0] || null
    }

    document.addEventListener('keydown', handleSelectAllShortcut)
    return () => document.removeEventListener('keydown', handleSelectAllShortcut)
  }, [selectableFileIds])

  const loadedCount = sortedCategoryFiles.length
  const displayTotal = typeof total === 'number' ? Math.max(total, loadedCount) : loadedCount
  const itemsCount = hasMore ? loadedCount + 1 : loadedCount
  const isItemLoaded = index => !hasMore || index < loadedCount
  const loadingMoreRef = useRef(false)
  const loadMoreItems = () => {
    if (loadingMoreRef.current || !hasMore) return Promise.resolve()
    loadingMoreRef.current = true
    return Promise.resolve(onLoadMore?.()).finally(() => {
      loadingMoreRef.current = false
    })
  }

  useEffect(() => {
    !isUnit && setRecentFilesVisible(true)
  }, [effectiveCategory])

  useEffect(() => {
    setSelectedFiles([])
    selectionAnchorRef.current = null
    setIsMobileSelectionMode(false)
  }, [effectiveFolder, effectiveCategory])

  useEffect(() => {
    if (selectedFiles.length === 0 && isMobileSelectionMode) {
      setIsMobileSelectionMode(false)
    }
  }, [selectedFiles.length, isMobileSelectionMode])

  const handleClearSelection = () => {
    setSelectedFiles([])
    selectionAnchorRef.current = null
    setIsMobileSelectionMode(false)
  }

  const handleActivateMobileSelectionMode = useCallback(() => {
    const isMobile = window.innerWidth <= 768
    if (isMobile) {
      setIsMobileSelectionMode(true)
    }
  }, [isMobileSelectionMode])

  const openSortMenu = event => {
    // On mobile, use bottom sheet instead of menu
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
    if (isMobile) {
      setSortBottomSheetOpen(true)
    } else {
      setSortMenuAnchorEl(event.currentTarget)
    }
  }

  const closeSortMenu = () => {
    setSortMenuAnchorEl(null)
    setSortBottomSheetOpen(false)
  }

  const getSortLabel = sortBy => {
    switch (sortBy) {
      case 'relevance':
        return 'Relevance'
      case 'newest':
        return 'Newest first'
      case 'oldest':
        return 'Oldest first'
      case 'name-asc':
        return 'Name (A-Z)'
      case 'name-desc':
        return 'Name (Z-A)'
      case 'type':
        return 'File type'
      case 'expiry-asc':
        return 'Expiry date (soonest)'
      case 'expiry-desc':
        return 'Expiry date (latest)'
      default:
        return 'Newest first'
    }
  }

  const gridRenderRef = useRef({})
  gridRenderRef.current = {
    fileTypes, isUnit, canEditUnit, toggleFileSelection, folders, fileCategories,
    effectiveCategory, effectiveFolder, isLegacyView, searchMode, viewMode,
    handleOpenPermissionsModal, handleOpenReclassifyModal, handleOpenViewedStatusModal,
    onOpenEditModal, searchQuery: searchCriteria?.query, isEstateAggregateContext, colorPalette
  }

  const GridItem = useCallback(
    ({ columnIndex, rowIndex, style, data }) => {
      const { items, columnCount, itemsCount, isItemLoaded, loadingMore, selectedFiles } = data
      const {
        fileTypes, isUnit, canEditUnit, toggleFileSelection, folders, fileCategories,
        effectiveCategory, effectiveFolder, isLegacyView, searchMode,
        handleOpenPermissionsModal, handleOpenReclassifyModal, handleOpenViewedStatusModal,
        onOpenEditModal, searchQuery, isEstateAggregateContext, colorPalette
      } = gridRenderRef.current
      const index = rowIndex * columnCount + columnIndex

      if (index >= itemsCount) {
        return null
      }

      const isMobile = data.isMobile || false
      const adjustedStyle = {
        ...style,
        left: isMobile ? 0 : style.left + 12,
        top: isMobile ? style.top : style.top + 8,
        width: isMobile ? '100%' : style.width - 24,
        height: isMobile ? 'auto' : style.height - 16,
        minHeight: isMobile ? 280 : undefined,
        position: 'absolute',
        overflow: 'visible'
      }

      if (!isItemLoaded(index)) {
        return (
          <div style={adjustedStyle}>
            {loadingMore && <LoadingFileCard />}
          </div>
        )
      }

      const file = items[index]

      let categoryColor = null

      // First, try to get color based on the file's actual category
      const fileCategoryId = file?.fileType?.fileCategoryId
      if (fileCategoryId && fileCategories && folders) {
        // Find which category the file belongs to
        const fileCategory = fileCategories.find(
          cat => cat.id === fileCategoryId
        )
        if (fileCategory && fileCategory.folderId) {
          // Find the folder that contains this category
          const folderIndex = folders.findIndex(
            folder => folder.id === fileCategory.folderId
          )
          if (folderIndex !== -1) {
            categoryColor = colorPalette[folderIndex % colorPalette.length]
          }
        }
      }

      // Fallback to previous logic if no category color found
      if (!categoryColor) {
        if (effectiveCategory && !isLegacyView && effectiveFolder) {
          // If in a folder view with selected category
          const allFolders = folders || []
          const folderIndex = allFolders.findIndex(
            folder => folder.id === effectiveFolder.id
          )

          if (folderIndex !== -1) {
            categoryColor = colorPalette[folderIndex % colorPalette.length]
          }
        } else if (effectiveFolder && !effectiveCategory) {
          // If viewing all files in a folder without a specific category
          const allFolders = folders || []
          const folderIndex = allFolders.findIndex(
            folder => folder.id === effectiveFolder.id
          )

          if (folderIndex !== -1) {
            categoryColor = colorPalette[folderIndex % colorPalette.length]
          }
        }
      }

      // Memoize isSelected to reduce re-renders during drag
      const isSelected = selectedFiles.includes(file.id)

      return (
        <div
          key={`grid-${file.id}`}
          style={adjustedStyle}
          className='file-card'
          data-file-id={file.id}
        >
          <GridItemContainer>
            <File
              isUnit={isUnit}
              canEditUnit={canEditUnit}
              file={file}
              type={getFileTypeName(
                pathOr('', ['fileType', 'id'], file),
                fileTypes,
                file.isPrivate
              )}
              isSharedWithUnits={file.isSharedWithUnits}
              viewMode='grid'
              showThumbnail={true}
              categoryColor={categoryColor}
              isSelected={isSelected}
              rowIndex={index}
              onSelect={toggleFileSelection}
              onOpenReclassifyModal={() => handleOpenReclassifyModal(file)}
              onOpenEditModal={onOpenEditModal}
              onOpenPermissionsModal={handleOpenPermissionsModal}
              onOpenViewedStatusModal={handleOpenViewedStatusModal}
              externalModalControl={true}
              searchQuery={searchQuery}
              searchMode={searchMode}
              readOnlyAggregate={isEstateAggregateContext}
              hideBulkSelect={false}
              sourceBpName={isEstateAggregateContext ? (file.buildingPassport?.name || 'Estate') : undefined}
            />
          </GridItemContainer>
        </div>
      )
    },
    []
  )

  const ListItem = useCallback(
    ({ index, style, data }) => {
      const { items, itemsCount, isItemLoaded, loadingMore, selectedFiles } = data
      const {
        fileTypes, isUnit, canEditUnit, toggleFileSelection, folders, fileCategories,
        effectiveCategory, effectiveFolder, isLegacyView, searchMode, viewMode,
        handleOpenPermissionsModal, handleOpenReclassifyModal, handleOpenViewedStatusModal,
        onOpenEditModal, searchQuery, isEstateAggregateContext, colorPalette
      } = gridRenderRef.current

      if (index >= itemsCount) {
        return null
      }

      const adjustedStyle = {
        ...style,
        padding: viewMode === 'list' ? '0' : '8px 0'
      }

      if (!isItemLoaded(index)) {
        return (
          <div style={adjustedStyle}>
            {loadingMore && <LoadingListItem />}
          </div>
        )
      }

      const file = items[index]

      let categoryColor = null

      // First, try to get color based on the file's actual category
      const fileCategoryId = file?.fileType?.fileCategoryId
      if (fileCategoryId && fileCategories && folders) {
        // Find which category the file belongs to
        const fileCategory = fileCategories.find(
          cat => cat.id === fileCategoryId
        )
        if (fileCategory && fileCategory.folderId) {
          // Find the folder that contains this category
          const folderIndex = folders.findIndex(
            folder => folder.id === fileCategory.folderId
          )
          if (folderIndex !== -1) {
            categoryColor = colorPalette[folderIndex % colorPalette.length]
          }
        }
      }

      // Fallback to previous logic if no category color found
      if (!categoryColor) {
        if (effectiveCategory && !isLegacyView && effectiveFolder) {
          // If in a folder view with selected category
          const allFolders = folders || []
          const folderIndex = allFolders.findIndex(
            folder => folder.id === effectiveFolder.id
          )

          if (folderIndex !== -1) {
            categoryColor = colorPalette[folderIndex % colorPalette.length]
          }
        } else if (effectiveFolder && !effectiveCategory) {
          // If viewing all files in a folder without a specific category
          const allFolders = folders || []
          const folderIndex = allFolders.findIndex(
            folder => folder.id === effectiveFolder.id
          )

          if (folderIndex !== -1) {
            categoryColor = colorPalette[folderIndex % colorPalette.length]
          }
        }
      }

      return (
        <div
          key={`list-${file.id}`}
          style={adjustedStyle}
          className='list-item'
        >
          <File
            isUnit={isUnit}
            canEditUnit={canEditUnit}
            file={file}
            type={getFileTypeName(
              pathOr('', ['fileType', 'id'], file),
              fileTypes,
              file.isPrivate
            )}
            isSharedWithUnits={file.isSharedWithUnits}
            viewMode='list'
            showThumbnail={false}
            categoryColor={categoryColor}
            isSelected={selectedFiles.includes(file.id)}
            onSelect={toggleFileSelection}
            rowIndex={index}
            onOpenReclassifyModal={() => handleOpenReclassifyModal(file)}
            onOpenEditModal={onOpenEditModal}
            onOpenPermissionsModal={handleOpenPermissionsModal}
            onOpenViewedStatusModal={handleOpenViewedStatusModal}
            externalModalControl={true}
            searchQuery={searchQuery}
            searchMode={searchMode}
            readOnlyAggregate={isEstateAggregateContext}
            hideBulkSelect={false}
            sourceBpName={isEstateAggregateContext ? (file.buildingPassport?.name || 'Estate') : undefined}
          />
        </div>
      )
    },
    []
  )

  const renderFileList = () => (
    <VirtualizedFilesWrapper
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onClick={handleContentClick}
      style={{ position: 'relative', userSelect: 'none' }}
    >
      {/* Drag selection overlay */}
      {isDragSelecting && (
        <SelectionOverlay
          style={{
            left: Math.max(
              dragViewport.left,
              dragViewport.left + Math.min(dragStart.x, dragEnd.x) - dragScrollOffset.x
            ),
            top: Math.max(
              dragViewport.top,
              dragViewport.top + Math.min(dragStart.y, dragEnd.y) - dragScrollOffset.y
            ),
            width: Math.max(
              0,
              Math.min(
                dragViewport.left + dragViewport.width,
                dragViewport.left + Math.max(dragStart.x, dragEnd.x) - dragScrollOffset.x
              ) - Math.max(
                dragViewport.left,
                dragViewport.left + Math.min(dragStart.x, dragEnd.x) - dragScrollOffset.x
              )
            ),
            height: Math.max(
              0,
              Math.min(
                dragViewport.top + dragViewport.height,
                dragViewport.top + Math.max(dragStart.y, dragEnd.y) - dragScrollOffset.y
              ) - Math.max(
                dragViewport.top,
                dragViewport.top + Math.min(dragStart.y, dragEnd.y) - dragScrollOffset.y
              )
            )
          }}
        >
          <DragSelectionHint>
            Hold CMD or CTRL to add to the selection
          </DragSelectionHint>
        </SelectionOverlay>
      )}
      {viewMode === 'list' && (
        <ListViewHeader>
          <HeaderCombined>File Name</HeaderCombined>
          <HeaderFileType>File Type</HeaderFileType>
          <HeaderFileDate>Date</HeaderFileDate>
          <HeaderUploader>Uploader</HeaderUploader>
          <HeaderActions></HeaderActions>
        </ListViewHeader>
      )}
      {viewMode === 'list' && groupByCategory ? (
        <GroupedList>
          {groupedFileSections.length === 0 && (
            <GroupEmpty>No documents to show.</GroupEmpty>
          )}
          {groupedFileSections.map(section => {
            const collapsed = collapsedGroups.has(section.id)
            return (
              <GroupSection key={section.id}>
                <GroupHeader type='button' onClick={() => toggleGroup(section.id)}>
                  {collapsed
                    ? <ChevronRightIcon style={{ fontSize: 18 }} />
                    : <ExpandMoreIcon style={{ fontSize: 18 }} />}
                  {section.color && <GroupSwatch style={{ background: section.color }} />}
                  <GroupName>{section.name}</GroupName>
                  <GroupCount>{section.files.length}</GroupCount>
                </GroupHeader>
                {!collapsed && (() => {
                  const showAll = fullyShownGroups.has(section.id)
                  const visibleFiles = showAll ? section.files : section.files.slice(0, GROUP_RENDER_CAP)
                  return (
                    <>
                      {visibleFiles.map((file, i) => (
                        <File
                          key={`grouped-${file.id}`}
                          isUnit={isUnit}
                          canEditUnit={canEditUnit}
                          file={file}
                          type={getFileTypeName(pathOr('', ['fileType', 'id'], file), fileTypes, file.isPrivate)}
                          isSharedWithUnits={file.isSharedWithUnits}
                          viewMode='list'
                          showThumbnail={false}
                          categoryColor={section.color}
                          isSelected={selectedFiles.includes(file.id)}
                          onSelect={toggleFileSelection}
                          rowIndex={i}
                          onOpenReclassifyModal={() => handleOpenReclassifyModal(file)}
                          onOpenEditModal={onOpenEditModal}
                          onOpenPermissionsModal={handleOpenPermissionsModal}
                          onOpenViewedStatusModal={handleOpenViewedStatusModal}
                          externalModalControl={true}
                          searchQuery={searchCriteria?.query}
                          searchMode={searchMode}
                          readOnlyAggregate={isEstateAggregateContext}
                          hideBulkSelect={false}
                          sourceBpName={isEstateAggregateContext ? (file.buildingPassport?.name || 'Estate') : undefined}
                        />
                      ))}
                      {!showAll && section.files.length > GROUP_RENDER_CAP && (
                        <ShowAllRow type='button' onClick={() => setFullyShownGroups(prev => new Set(prev).add(section.id))}>
                          Show all {section.files.length} documents in {section.name}
                        </ShowAllRow>
                      )}
                    </>
                  )
                })()}
              </GroupSection>
            )
          })}
        </GroupedList>
      ) : (
      <AutoSizer>
        {({ height, width }) => {
          const isMobile =
            width <= 768 &&
            (typeof window === 'undefined' || window.innerWidth <= 768)
          const headerHeight = viewMode === 'list' ? (isMobile ? 0 : 28) : 0

          let adjustedHeight
          if (isMobile) {
            const windowHeight = typeof window !== 'undefined' ? window.innerHeight : 667
            adjustedHeight = Math.max(520, windowHeight - 160) - headerHeight
          } else {
            adjustedHeight = (height || 1200) - headerHeight
          }

          if (!adjustedHeight || adjustedHeight < 400) {
            const windowHeight = typeof window !== 'undefined' ? window.innerHeight : 667
            adjustedHeight = Math.max(500, windowHeight - 400)
            console.log('AutoSizer adjusted height:', { height, adjustedHeight, isMobile, headerHeight, windowHeight })
          }

          if (viewMode === 'grid') {
            // Keep the old desktop layout logic. The only special case is real mobile viewports.
            let itemWidth = GRID_ITEM_WIDTH
            let itemHeight = GRID_ITEM_HEIGHT

            if (isMobile) {
              itemWidth = width
              itemHeight = 290
            } else if (width < 1200) {
              itemWidth = 240
            }

            const columnCount = isMobile ? 1 : Math.max(1, Math.floor(width / itemWidth))

            if (!isMobile && columnCount === 1) {
              itemWidth = width
            }

            const rowCount = Math.ceil(itemsCount / columnCount)
            gridLayoutRef.current = { columnCount, itemHeight }

            return (
              <InfiniteLoader
                isItemLoaded={isItemLoaded}
                itemCount={itemsCount}
                loadMoreItems={loadMoreItems}
                threshold={10}
              >
                {({ onItemsRendered, ref }) => {
                  const onGridItemsRendered = ({
                    visibleRowStartIndex,
                    visibleRowStopIndex,
                    visibleColumnStartIndex,
                    visibleColumnStopIndex
                  }) => {
                    const startIndex = visibleRowStartIndex * columnCount
                    const stopIndex = Math.min(
                      (visibleRowStopIndex + 1) * columnCount - 1,
                      itemsCount - 1
                    )

                    onItemsRendered({
                      visibleStartIndex: startIndex,
                      visibleStopIndex: stopIndex
                    })
                  }

                  return (
                    <FixedSizeGrid
                      ref={ref}
                      outerRef={virtualizedScrollRef}
                      columnCount={columnCount}
                      columnWidth={itemWidth}
                      height={adjustedHeight}
                      rowCount={rowCount}
                      rowHeight={itemHeight}
                      width={width}
                      onItemsRendered={onGridItemsRendered}
                      onScroll={handleVirtualizedScroll}
                      itemData={{
                        items: sortedCategoryFiles,
                        columnCount,
                        isMobile,
                        itemsCount,
                        isItemLoaded,
                        loadingMore,
                        selectedFiles
                      }}
                    >
                      {GridItem}
                    </FixedSizeGrid>
                  )
                }}
              </InfiniteLoader>
            )
          } else {
            if (isMobile) {
              return (
                <div
                  ref={virtualizedScrollRef}
                  onScroll={e => {
                    handleVirtualizedScroll(e)
                    const el = e.currentTarget
                    if (hasMore && el.scrollHeight - el.scrollTop - el.clientHeight < 300) {
                      loadMoreItems()
                    }
                  }}
                  style={{
                    height: adjustedHeight,
                    width: width,
                    overflowY: 'auto',
                    paddingBottom: '80px', // Extra padding for FAB
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  {sortedCategoryFiles.map((file, index) => {
                    let categoryColor = null
                    const fileCategoryId = file?.fileType?.fileCategoryId
                    if (fileCategoryId && fileCategories && folders) {
                      const fileCategory = fileCategories.find(cat => cat.id === fileCategoryId)
                      if (fileCategory && fileCategory.folderId) {
                        const folderIndex = folders.findIndex(folder => folder.id === fileCategory.folderId)
                        if (folderIndex !== -1) {
                          categoryColor = colorPalette[folderIndex % colorPalette.length]
                        }
                      }
                    }

                    return (
                      <div
                        key={file.id}
                        style={{
                          marginBottom: '8px',
                          width: '100%'
                        }}
                      >
                        <File
                          isUnit={isUnit}
                          canEditUnit={canEditUnit}
                          file={file}
                          type={getFileTypeName(
                            pathOr('', ['fileType', 'id'], file),
                            fileTypes,
                            file.isPrivate
                          )}
                          isSharedWithUnits={file.isSharedWithUnits}
                          viewMode='list'
                          showThumbnail={false}
                          categoryColor={categoryColor}
                          isSelected={selectedFiles.includes(file.id)}
                          onSelect={toggleFileSelection}
                          rowIndex={index}
                          onOpenReclassifyModal={() => handleOpenReclassifyModal(file)}
                          onOpenEditModal={onOpenEditModal}
                          onOpenPermissionsModal={handleOpenPermissionsModal}
                          onOpenViewedStatusModal={handleOpenViewedStatusModal}
                          externalModalControl={true}
                          searchQuery={searchCriteria?.query}
                          searchMode={searchMode}
                          isMobileSelectionMode={isMobileSelectionMode}
                          onActivateMobileSelectionMode={handleActivateMobileSelectionMode}
                          readOnlyAggregate={isEstateAggregateContext}
                          hideBulkSelect={false}
                          sourceBpName={isEstateAggregateContext ? (file.buildingPassport?.name || 'Estate') : undefined}
                        />
                      </div>
                    )
                  })}
                  {sortedCategoryFiles.length > 0 && loadingMore && (
                    <LoadingListItem />
                  )}
                  {/* Empty state within list if needed */}
                  {sortedCategoryFiles.length === 0 && isLoadingFiles && (
                    <div style={{ display: 'flex', gap: 16, padding: 16, flexWrap: 'wrap' }}>
                      {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} style={{ width: 220, borderRadius: 8, overflow: 'hidden', border: '1px solid #eee' }}>
                          <Skeleton variant='rectangular' width='100%' height={160} />
                          <div style={{ padding: 10 }}>
                            <Skeleton variant='text' width='80%' height={18} />
                            <Skeleton variant='text' width='50%' height={14} sx={{ mt: 0.5 }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {sortedCategoryFiles.length === 0 && !isLoadingFiles && (
                    <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                      No files to display
                    </div>
                  )}
                </div>
              )
            } else {
              return (
                <InfiniteLoader
                  isItemLoaded={isItemLoaded}
                  itemCount={itemsCount}
                  loadMoreItems={loadMoreItems}
                  threshold={10}
                >
                  {({ onItemsRendered, ref }) => (
                    <FixedSizeList
                      ref={ref}
                      outerRef={virtualizedScrollRef}
                      height={adjustedHeight}
                      itemCount={itemsCount}
                      itemSize={LIST_ROW_HEIGHT} // Standard desktop row height
                      width={width}
                      onItemsRendered={onItemsRendered}
                      onScroll={handleVirtualizedScroll}
                      itemData={{ items: sortedCategoryFiles, itemsCount, isItemLoaded, loadingMore, selectedFiles }}
                    >
                      {ListItem}
                    </FixedSizeList>
                  )}
                </InfiniteLoader>
              )
            }
          }
        }}
      </AutoSizer>
      )}
    </VirtualizedFilesWrapper>
  )

  const renderBreadcrumbs = () => {
    // Only show "FILES > search" when there's actually a text search query or multiple filters
    const hasActualSearch =
      searchCriteria?.isActive &&
      (
        (searchCriteria.query && searchCriteria.query.length >= 3) ||
        (searchCriteria.fileTypeIds && searchCriteria.fileTypeIds.length > 0) ||
        (searchCriteria.uploaderIds && searchCriteria.uploaderIds.length > 0) ||
        (searchCriteria.tagIds && searchCriteria.tagIds.length > 0) ||
        (searchCriteria.fileTags && searchCriteria.fileTags.length > 0) ||
        isSearching
      )

    if (hasActualSearch) {
      const folderName =
        selectedFolder?.name ||
        selectedCategory?.name ||
        null

      // True if user is currently scoped to a folder/category AND results actually exist there.
      // In aggregate (portfolio/estate) views the loaded page is capped, so counts must come
      // from the server totals (`total` = selected scope, `totalBpResults` = whole container),
      // not from the loaded `files` length.
      const isSpecificScope =
        !!(selectedFolder && selectedFolder.id !== 'all_files') || !!selectedCategory
      const currentResultsCount = isEstateAggregateContext
        ? (isSpecificScope && typeof total === 'number' ? total : 0)
        : (effectiveCategory?.files?.length || 0)
      const globalResultsCount = isEstateAggregateContext && typeof totalBpResults === 'number'
        ? totalBpResults
        : totalResultsCount
      const isLimitedToFolder = !!(selectedFolder || selectedCategory) && currentResultsCount > 0

      const shownCount = currentResultsCount > 0
        ? currentResultsCount
        : globalResultsCount

      return (
        <HeaderTitleWrapper>
          <div>
            <BreadcrumbLine>
              <BreadcrumbSmall>FILES</BreadcrumbSmall>
              <ChevronRightIcon fontSize="small" />
              <SearchLabel>SEARCH</SearchLabel>
            </BreadcrumbLine>

            {shownCount > 0 && (
              <ResultsSummary>
                <strong>{shownCount}</strong>{' '}
                {shownCount === 1 ? 'result' : 'results'}
                {isLimitedToFolder && folderName && (
                  <>
                    {' '}found in{' '}
                    <FolderAccent>{folderName.replace(/\s0$/, '') || 'Uncategorised Files'}</FolderAccent>
                  </>
                )}
                {!isLimitedToFolder && globalResultsCount > 0 && effectiveFolder?.id !== 'all_files' && (
                  <> found across this {searchScopeLabel} </>
                )}
                {isLimitedToFolder && globalResultsCount > currentResultsCount && effectiveFolder?.id !== 'all_files' && (
                  <>
                    ·{' '}
                    <ShowAllLink
                      onClick={() => {
                        onFolderSelect?.(
                          {
                            id: 'all_files',
                            name: 'All Files',
                            isVirtual: true,
                          },
                          true
                        );
                      }}
                    >
                      {globalResultsCount} {globalResultsCount === 1 ? 'result' : 'results'} found in the whole {searchScopeLabel}
                    </ShowAllLink>
                  </>
                )}
                {!isEstateAggregateContext && isSearchTruncated && !isSearchLoadingRedux && (
                  <>
                    {' '}· Showing the first 300 results.{' '}
                    <ShowAllLink onClick={handleLoadAllResults}>
                      Load all results
                    </ShowAllLink>
                  </>
                )}
                {!isEstateAggregateContext && isSearchLoadingRedux && (
                  <>
                    {' '}· <CircularProgress size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Loading all results...
                  </>
                )}
              </ResultsSummary>
            )}
          </div>

          {isSearching && (
            <ClearFilterButton
              onClick={handleClearFilter}
              title='Click to clear filter and show all files'
            >
              Clear Filter
            </ClearFilterButton>
          )}
        </HeaderTitleWrapper>
      )
    }

    // When a category is selected, show "folder name > category name"
    if (hasSelectedCategory) {
      let folderName = 'Files'

      if (isLegacyView) {
        folderName = 'Legacy Files'
      } else if (effectiveFolder?.name) {
        folderName = effectiveFolder.name
      } else if (selectedFolder?.name) {
        folderName = selectedFolder.name
      } else if (effectiveCategory?.folderId && folders) {
        // Find the folder name using the category's folderId
        const folder = folders.find(f => f.id === effectiveCategory.folderId)
        folderName = folder?.name || 'Files'
      }

      // Get total file count from API
      const totalFileCount = totalBpResults || 0

      return (
        <HeaderTitleWrapper>
          <SectionHeader open={true} onClick={handleBackToCategories}>
            {folderName}
          </SectionHeader>
          <BreadcrumbsSign>&gt;</BreadcrumbsSign>
          <CurrentFolderName>{effectiveCategory.name}</CurrentFolderName>
          {totalFileCount > 0 && (
            <FilesCountBadge
              onClick={(e) => {
                e.stopPropagation()
                onFolderSelect?.({
                  id: 'all_files',
                  name: 'All Files',
                  isVirtual: true
                }, true)
              }}
            >
              {totalFileCount} {totalFileCount === 1 ? 'file' : 'files'}
            </FilesCountBadge>
          )}
        </HeaderTitleWrapper>
      )
    }

    // When categories are available but none specifically selected, show folder name
    if (hasCategoriesAvailable && currentViewCategories.length > 0) {
      let folderName = 'Files'

      if (isLegacyView) {
        folderName = 'Legacy Files'
      } else if (effectiveFolder?.name) {
        folderName = effectiveFolder.name
      } else if (selectedFolder?.name) {
        folderName = selectedFolder.name
      } else if (currentViewCategories[0]?.folderId && folders) {
        // Find the folder name using the first category's folderId
        const folder = folders.find(f => f.id === currentViewCategories[0].folderId)
        folderName = folder?.name || 'Files'
      }

      // Get total file count from API
      const totalFileCount = totalBpResults || 0

      return (
        <HeaderTitleWrapper>
          <SectionHeader
            as={onOpenMobileDrawer ? 'button' : 'div'}
            onClick={onOpenMobileDrawer || undefined}
            $isClickable={!!onOpenMobileDrawer}
          >
            {folderName}
          </SectionHeader>
          {totalFileCount > 0 && (
            <FilesCountBadge
              onClick={() => {
                onFolderSelect?.({
                  id: 'all_files',
                  name: 'All Files',
                  isVirtual: true
                }, true)
              }}
            >
              {totalFileCount} {totalFileCount === 1 ? 'file' : 'files'}
            </FilesCountBadge>
          )}
        </HeaderTitleWrapper>
      )
    }

    // Default view - show "Folders"
    const totalFileCount = totalBpResults || 0
    return (
      <HeaderTitleWrapper>
        <SectionHeader>
          {isLegacyView ? 'Legacy Files' : 'Folders'}
        </SectionHeader>
        {totalFileCount > 0 && (
          <FilesCountBadge
            onClick={() => {
              onFolderSelect?.({
                id: 'all_files',
                name: 'All Files',
                isVirtual: true
              }, true)
            }}
          >
            {totalFileCount} {totalFileCount === 1 ? 'file' : 'files'}
          </FilesCountBadge>
        )}
      </HeaderTitleWrapper>
    )
  }

  // Show loading while initial data is loading
  const isInitialLoading =
    !folderTabs.length && !isLegacyView && isFetchingFiles

  if (isInitialLoading) {
    return (
      <SectionWrapper withMargin={isUnit || isNil(effectiveCategory)}>
        <LoadingContainer>
          <AnimatedIconContainer>
            <BounceIcon delay='0s'>
              <FolderIcon sx={{ fontSize: 32, color: '#666' }} />
            </BounceIcon>
            <BounceIcon delay='0.3s'>
              <FolderOpenIcon sx={{ fontSize: 28, color: '#888' }} />
            </BounceIcon>
            <BounceIcon delay='0.6s'>
              <DescriptionIcon sx={{ fontSize: 24, color: '#999' }} />
            </BounceIcon>
          </AnimatedIconContainer>
          <LoadingText>Loading folders and categories...</LoadingText>
          <ProgressContainer>
            <ProgressBar />
          </ProgressContainer>
        </LoadingContainer>
      </SectionWrapper>
    )
  }

  return (
    <SectionWrapper withMargin={isUnit || isNil(effectiveCategory)}>
      <HeaderWrapper>{renderBreadcrumbs()}</HeaderWrapper>
      {!isUnit && bp?.id && <LockedDocumentsBanner bpId={bp.id} />}
      {(hasSelectedCategory || hasSelectedFolder) && (
        <>
          <ViewToggle>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {/* Grid/List view toggles - hidden on phones while selecting so the selection controls fit */}
              <ViewModeToggles $hideOnMobile={selectedFiles.length > 0}>
              <Tooltip title='Grid view'>
                <StyledIconButton
                  isActive={viewMode === 'grid'}
                  onClick={() => setViewMode('grid')}
                  data-testid='files-grid-view'
                >
                  <GridViewIcon />
                </StyledIconButton>
              </Tooltip>
              <Tooltip title='List view'>
                <StyledIconButton
                  data-tour-id='files.list-view-toggle'
                  isActive={viewMode === 'list'}
                  onClick={() => setViewMode('list')}
                >
                  <ListIcon />
                </StyledIconButton>
              </Tooltip>
              {viewMode === 'list' && (
                <Tooltip title={groupByCategory ? 'Ungroup: show a flat list' : 'Group documents by category'}>
                  <StyledIconButton
                    data-tour-id='files.group-by-category'
                    isActive={groupByCategory}
                    onClick={() => setGroupByCategory(v => !v)}
                  >
                    <SegmentOutlinedIcon />
                  </StyledIconButton>
                </Tooltip>
              )}
              </ViewModeToggles>

              {/* Selected files count + select/deselect all */}
              {selectedFiles.length > 0 && (
                <SelectedFilesCount>
                  {selectedFiles.length}
                  <SelectedText>selected</SelectedText>
                  {isCompactToolbarViewport && (
                    <ChipClearButton type='button' onClick={handleClearSelection} aria-label='Clear selection'>
                      <CloseRoundedIcon />
                    </ChipClearButton>
                  )}
                </SelectedFilesCount>
              )}
              <Tooltip title={allSelectableSelected ? 'Deselect all' : 'Select all'}>
                <SelectAllButton
                  $isDeselect={allSelectableSelected}
                  onClick={() => {
                    if (allSelectableSelected) {
                      handleClearSelection()
                    } else {
                      setSelectedFiles(selectableFileIds)
                    }
                  }}
                >
                  <SelectAllText $isDeselect={allSelectableSelected}>
                    {allSelectableSelected
                      ? (isCompactToolbarViewport ? 'Deselect' : 'Deselect all')
                      : (isCompactToolbarViewport ? 'Select' : 'Select all')}
                  </SelectAllText>
                </SelectAllButton>
              </Tooltip>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <SortButton onClick={openSortMenu}>
                <SortIcon fontSize='small' />
                <SortText>
                  {isCompactToolbarViewport
                    ? getSortLabel(sortBy)
                    : `Sort: ${getSortLabel(sortBy)}`}
                </SortText>
              </SortButton>

              <Menu
                anchorEl={sortMenuAnchorEl}
                open={Boolean(sortMenuAnchorEl)}
                onClose={closeSortMenu}
              >
                {sortedCategoryFiles.some(f => f.searchScore !== undefined) && (
                  <MenuItem
                    selected={sortBy === 'relevance'}
                    onClick={() => {
                      setSortBy('relevance')
                      closeSortMenu()
                    }}
                  >
                    Relevance
                  </MenuItem>
                )}
                <MenuItem
                  selected={sortBy === 'newest'}
                  onClick={() => {
                    setSortBy('newest')
                    closeSortMenu()
                  }}
                >
                  Newest first
                </MenuItem>
                <MenuItem
                  selected={sortBy === 'oldest'}
                  onClick={() => {
                    setSortBy('oldest')
                    closeSortMenu()
                  }}
                >
                  Oldest first
                </MenuItem>
                <MenuItem
                  selected={sortBy === 'name-asc'}
                  onClick={() => {
                    setSortBy('name-asc')
                    closeSortMenu()
                  }}
                >
                  Name (A-Z)
                </MenuItem>
                <MenuItem
                  selected={sortBy === 'name-desc'}
                  onClick={() => {
                    setSortBy('name-desc')
                    closeSortMenu()
                  }}
                >
                  Name (Z-A)
                </MenuItem>
                <MenuItem
                  selected={sortBy === 'type'}
                  onClick={() => {
                    setSortBy('type')
                    closeSortMenu()
                  }}
                >
                  File type
                </MenuItem>
                <MenuItem
                  selected={sortBy === 'expiry-asc'}
                  onClick={() => {
                    setSortBy('expiry-asc')
                    closeSortMenu()
                  }}
                >
                  Expiry date (soonest)
                </MenuItem>
                <MenuItem
                  selected={sortBy === 'expiry-desc'}
                  onClick={() => {
                    setSortBy('expiry-desc')
                    closeSortMenu()
                  }}
                >
                  Expiry date (latest)
                </MenuItem>
              </Menu>
            </div>

            {/* Bulk actions - own full-width row of the bar while files are selected; a 3-column grid on narrow screens */}
            {selectedFiles.length > 0 && (
              <BulkActions role='toolbar' aria-label='Bulk actions'>
                {canBulkDownload && (
                  <BulkActionButton
                    type='button'
                    onClick={handleBulkDownload}
                    disabled={Boolean(bulkBusyAction)}
                  >
                    {bulkBusyAction === 'download'
                      ? <CircularProgress size={14} color='inherit' />
                      : <FileDownloadOutlinedIcon />}
                    Download
                  </BulkActionButton>
                )}
                {canBulkManageFiles && (
                  <BulkActionButton type='button' onClick={() => setBulkPermissionsModalOpen(true)}>
                    <ManageAccountsOutlinedIcon />
                    {isCompactToolbarViewport ? 'Permissions' : 'Manage permissions'}
                  </BulkActionButton>
                )}
                {bulkCanEdit && (
                  <>
                    <BulkActionButton type='button' onClick={() => setBulkEditModalOpen(true)}>
                      <EditIcon />
                      Edit
                    </BulkActionButton>
                    <BulkActionButton type='button' onClick={() => setBulkShareModalOpen(true)}>
                      <ShareIcon />
                      Share
                    </BulkActionButton>
                    <BulkActionButton type='button' onClick={() => setBulkUnsharedModalOpen(true)}>
                      <LinkOffIcon />
                      Unshare
                    </BulkActionButton>
                  </>
                )}
                {canBulkManageFiles && (
                  <BulkActionButton
                    type='button'
                    $danger
                    onClick={() => setBulkRemoveModalOpen(true)}
                    disabled={Boolean(bulkBusyAction)}
                  >
                    <DeleteOutlineIcon />
                    Remove
                  </BulkActionButton>
                )}
                {bulkBlockedHint && <BulkBlockedHint>{bulkBlockedHint}</BulkBlockedHint>}
                {!isCompactToolbarViewport && (
                  <>
                    <BulkActionsSpacer />
                    <Tooltip title='Clear selection'>
                      <ActionIconButton onClick={handleClearSelection} aria-label='Clear selection'>
                        <CloseRoundedIcon fontSize='small' />
                      </ActionIconButton>
                    </Tooltip>
                  </>
                )}
              </BulkActions>
            )}
          </ViewToggle>

          {/* Bottom Sheet for Sort on Mobile */}
          {typeof window !== 'undefined' && window.innerWidth <= 768 && ReactDOM.createPortal(
            <>
              {sortBottomSheetOpen && (
                <>
                  <SortBottomSheetOverlay onClick={closeSortMenu} />
                  <SortBottomSheet>
                    <SortBottomSheetHandle />
                    <SortBottomSheetHeader>
                      <SortBottomSheetTitle>Sort files</SortBottomSheetTitle>
                      <SortBottomSheetCloseButton onClick={closeSortMenu}>
                        ×
                      </SortBottomSheetCloseButton>
                    </SortBottomSheetHeader>
                    <SortBottomSheetContent>
                      {sortedCategoryFiles.some(f => f.searchScore !== undefined) && (
                        <SortBottomSheetItem
                          selected={sortBy === 'relevance'}
                          onClick={() => {
                            setSortBy('relevance')
                            closeSortMenu()
                          }}
                        >
                          <SortBottomSheetText>Relevance</SortBottomSheetText>
                        </SortBottomSheetItem>
                      )}
                      <SortBottomSheetItem
                        selected={sortBy === 'newest'}
                        onClick={() => {
                          setSortBy('newest')
                          closeSortMenu()
                        }}
                      >
                        <SortBottomSheetText>Newest first</SortBottomSheetText>
                      </SortBottomSheetItem>
                      <SortBottomSheetItem
                        selected={sortBy === 'oldest'}
                        onClick={() => {
                          setSortBy('oldest')
                          closeSortMenu()
                        }}
                      >
                        <SortBottomSheetText>Oldest first</SortBottomSheetText>
                      </SortBottomSheetItem>
                      <SortBottomSheetItem
                        selected={sortBy === 'name-asc'}
                        onClick={() => {
                          setSortBy('name-asc')
                          closeSortMenu()
                        }}
                      >
                        <SortBottomSheetText>Name (A-Z)</SortBottomSheetText>
                      </SortBottomSheetItem>
                      <SortBottomSheetItem
                        selected={sortBy === 'name-desc'}
                        onClick={() => {
                          setSortBy('name-desc')
                          closeSortMenu()
                        }}
                      >
                        <SortBottomSheetText>Name (Z-A)</SortBottomSheetText>
                      </SortBottomSheetItem>
                      <SortBottomSheetItem
                        selected={sortBy === 'type'}
                        onClick={() => {
                          setSortBy('type')
                          closeSortMenu()
                        }}
                      >
                        <SortBottomSheetText>File type</SortBottomSheetText>
                      </SortBottomSheetItem>
                      <SortBottomSheetItem
                        selected={sortBy === 'expiry-asc'}
                        onClick={() => {
                          setSortBy('expiry-asc')
                          closeSortMenu()
                        }}
                      >
                        <SortBottomSheetText>Expiry date (soonest)</SortBottomSheetText>
                      </SortBottomSheetItem>
                      <SortBottomSheetItem
                        selected={sortBy === 'expiry-desc'}
                        onClick={() => {
                          setSortBy('expiry-desc')
                          closeSortMenu()
                        }}
                      >
                        <SortBottomSheetText>Expiry date (latest)</SortBottomSheetText>
                      </SortBottomSheetItem>
                    </SortBottomSheetContent>
                  </SortBottomSheet>
                </>
              )}

            </>,
            document.body
          )}
          <div style={{ paddingTop: 20 }} />
        </>
      )}

      {searchCriteria?.isActive && !isSearchLoadingRedux && (
        <SearchStatusBanner $empty={sortedCategoryFiles.length === 0}>
          <SearchStatusContent>
            <SearchStatusDot $success={sortedCategoryFiles.length > 0} $empty={sortedCategoryFiles.length === 0} />
            {sortedCategoryFiles.length > 0 ? (
              <SearchStatusText>
                <strong>{displayTotal.toLocaleString('en-GB')}</strong> {displayTotal === 1 ? 'file' : 'files'} found
              </SearchStatusText>
            ) : (
              <SearchStatusText $empty>No results found for your search</SearchStatusText>
            )}
          </SearchStatusContent>
        </SearchStatusBanner>
      )}

      {isFetchingFiles && sortedCategoryFiles.length === 0 ? (
        <CategoryLoadingContainer>
          <FloatingIconsContainer>
            <FloatingIcon delay="0s">
              <FolderIcon sx={{ fontSize: 32, color: '#666' }} />
            </FloatingIcon>
            <FloatingIcon delay="0.8s">
              <InsertDriveFileIcon sx={{ fontSize: 28, color: '#888' }} />
            </FloatingIcon>
            <FloatingIcon delay="1.6s">
              <DescriptionIcon sx={{ fontSize: 30, color: '#777' }} />
            </FloatingIcon>
          </FloatingIconsContainer>
          <PulsingText>Loading...</PulsingText>
          <LoadingDots>
            <Dot delay="0s">●</Dot>
            <Dot delay="0.2s">●</Dot>
            <Dot delay="0.4s">●</Dot>
          </LoadingDots>
        </CategoryLoadingContainer>
      ) : (hasSelectedCategory || hasCategoriesAvailable || isAllFilesFolder) ? (
        <>
          {sortedCategoryFiles.length > 0 ? (
            <>
              {renderFileList()}
              <FileCountInfo>
                Showing {loadedCount.toLocaleString('en-GB')} of{' '}
                {displayTotal.toLocaleString('en-GB')} {displayTotal === 1 ? 'file' : 'files'}
                {effectiveCategory && !searchCriteria?.isActive && (
                  <span> in "{effectiveCategory.name}"</span>
                )}
                {searchCriteria?.isActive && (
                  <span> matching your search criteria</span>
                )}
                {loadingMore && (
                  <span>
                    {' '}· <CircularProgress size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Loading more...
                  </span>
                )}
              </FileCountInfo>
            </>
          ) : isLoadingFiles ? (
            <div style={{ display: 'flex', gap: 16, padding: 16, flexWrap: 'wrap' }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} style={{ width: 220, borderRadius: 8, overflow: 'hidden', border: '1px solid #eee' }}>
                  <Skeleton variant='rectangular' width='100%' height={160} />
                  <div style={{ padding: 10 }}>
                    <Skeleton variant='text' width='80%' height={18} />
                    <Skeleton variant='text' width='50%' height={14} sx={{ mt: 0.5 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyFolderDisplay>
              <EmptyFolderIcon>📄</EmptyFolderIcon>
              <EmptyFolderTitle>No Files Found</EmptyFolderTitle>
              <EmptyFolderMessage>
                {effectiveCategory?.name
                  ? `The category "${effectiveCategory.name}" doesn't contain any files yet.`
                  : "This folder doesn't contain any files yet."
                }
              </EmptyFolderMessage>
            </EmptyFolderDisplay>
          )}
        </>
      ) : (
        <CategoryLoadingContainer>
          <FloatingIconsContainer>
            <FloatingIcon delay="0s">
              <FolderIcon sx={{ fontSize: 32, color: '#666' }} />
            </FloatingIcon>
            <FloatingIcon delay="0.8s">
              <InsertDriveFileIcon sx={{ fontSize: 28, color: '#888' }} />
            </FloatingIcon>
            <FloatingIcon delay="1.6s">
              <DescriptionIcon sx={{ fontSize: 30, color: '#777' }} />
            </FloatingIcon>
          </FloatingIconsContainer>
          <PulsingText>Loading...</PulsingText>
          <LoadingDots>
            <Dot delay="0s">●</Dot>
            <Dot delay="0.2s">●</Dot>
            <Dot delay="0.4s">●</Dot>
          </LoadingDots>
        </CategoryLoadingContainer>
      )}
      <ShareBulkFilesModal
        open={bulkShareModalOpen}
        onClose={() => setBulkShareModalOpen(false)}
        selectedFiles={selectedFiles}
        bp={(isEstateAggregateContext ? bulkTargetBp : null) ?? bp}
      />
      <UnsharedBulkFilesModal
        open={bulkUnsharedModalOpen}
        onClose={() => setBulkUnsharedModalOpen(false)}
        selectedFiles={selectedFiles}
        bp={(isEstateAggregateContext ? bulkTargetBp : null) ?? bp}
      />
      <UnshareAllForEmailModal
        open={unshareAllModalOpen}
        onClose={() => setUnshareAllModalOpen(false)}
        bpId={bp?.id}
      />
      <BulkEditFileModal
        open={bulkEditModalOpen}
        onClose={() => setBulkEditModalOpen(false)}
        selectedFiles={selectedFiles}
        bp={(isEstateAggregateContext ? bulkTargetBp : null) ?? bp}
      />
      <FilePermissionsModal
        open={bulkPermissionsModalOpen}
        onClose={() => setBulkPermissionsModalOpen(false)}
        files={selectedFileObjects}
        bp={bulkBp}
        onSaved={refreshAfterBulkChange}
      />
      {bulkRemoveModalOpen && (
        <Modal
          open={bulkRemoveModalOpen}
          title={`Remove ${selectedFiles.length} file${selectedFiles.length === 1 ? '' : 's'}`}
          onClose={() => setBulkRemoveModalOpen(false)}
          onCancel={() => setBulkRemoveModalOpen(false)}
          onSubmit={handleBulkRemove}
          submitButtonText={bulkBusyAction === 'remove' ? 'Removing…' : 'Remove'}
          disabled={bulkBusyAction === 'remove'}
          withSubmit
          withCancel
        >
          Do you really want to remove the {selectedFiles.length} selected file{selectedFiles.length === 1 ? '' : 's'}?
        </Modal>
      )}
      <ReclassifyFileModal
        open={reclassifyModalOpen}
        onClose={handleCloseReclassifyModal}
        file={reclassifyFile}
        onOpenEditModal={onOpenEditModal}
        bp={(isEstateAggregateContext ? reclassifyFile?.buildingPassport : null) ?? bp}
      />
      <FilePermissionsModal
        open={Boolean(permissionsFile)}
        onClose={() => setPermissionsFile(null)}
        file={permissionsFile}
        bp={(isEstateAggregateContext ? permissionsFile?.buildingPassport : null) ?? bp}
      />
      {viewedStatusFile && (
        <ViewedStatusModal
          file={viewedStatusFile}
          canSeeHistory={isEstateAggregateContext ? viewedStatusFile?.buildingPassport?.isOwner : undefined}
          externalOpen
          onExternalClose={() => setViewedStatusFile(null)}
        />
      )}
    </SectionWrapper>
  )
}

const SortBottomSheetOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99999;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const SortBottomSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-radius: 20px 20px 0 0;
  z-index: 100000;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  padding-bottom: env(safe-area-inset-bottom);

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`

const SortBottomSheetHandle = styled.div`
  width: 40px;
  height: 4px;
  background: #d0d0d0;
  border-radius: 2px;
  margin: 12px auto 8px;
`

const SortBottomSheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
`

const SortBottomSheetTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  flex: 1;
`

const SortBottomSheetCloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 28px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f0f0;
  }
`

const SortBottomSheetContent = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 0;
`

const SortBottomSheetItem = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  background: ${({ selected }) => (selected ? '#f0f9ff' : 'transparent')};

  &:hover {
    background-color: ${({ selected }) => (selected ? '#f0f9ff' : '#f5f5f5')};
  }

  &:active {
    background-color: #e8e8e8;
  }
`

const SortBottomSheetText = styled.span`
  font-size: 16px;
  color: ${({ selected }) => (selected ? '#33becc' : '#1f2937')};
  font-weight: ${({ selected }) => (selected ? '600' : '400')};
`

export default FileCategories

const FilesWrapper = styled.div`
  box-sizing: border-box;
  max-width: 100%;
  display: ${props => (props.viewMode === 'grid' ? 'grid' : 'flex')};
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  grid-gap: 16px;
  align-items: start;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
`

const SectionWrapper = styled.div`
  margin-top: ${({ withMargin }) => (withMargin ? '30px' : 0)};
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    margin-top: ${({ withMargin }) => (withMargin ? '0' : 0)};
    height: auto;
    max-height: none;
    overflow: visible;
    flex: 1 1 auto;
    min-height: calc(100vh - 250px);
    padding-bottom: 0;
    background-color: white;
  }
`

const TabsContainer = styled.div`
  margin-bottom: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[500]};
  width: 100%;
  overflow: hidden;
`

const StyledTabs = styled(Tabs)`
  & .MuiTabs-indicator {
    background-color: ${({ activeColor, theme }) =>
    activeColor || theme.colors.primary};
  }

  & .MuiTabs-flexContainer {
    flex-wrap: wrap;
  }

  & .MuiTabs-scroller {
    overflow: visible !important;
  }
`

const StyledTab = styled(Tab)`
  text-transform: none !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  min-height: 50px !important;
  padding: 4px 10px !important;
  min-width: 50px !important;
  max-width: 200px !important;

  &:not(:last-child) {
    &::after {
      content: '';
      position: absolute;
      top: 12px;
      bottom: 12px;
      right: 0;
      width: 1px;
      background-color: ${({ theme }) => theme.colors.grey[600]};
    }
  }

  &.Mui-selected {
    color: #000 !important;
  }
`

const StyledFolderTabIcon = styled(FolderOpenIcon)`
  font-size: 18px !important;
  margin-right: 4px;
  flex-shrink: 0;
`
const FoldersList = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  grid-gap: 12px;
  margin-top: 20px;
  padding: 6px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    grid-gap: 14px;
    padding: 8px;
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    grid-gap: 16px;
  }
`

const FolderWrapper = styled.div`
  box-sizing: border-box;
  height: 64px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    border-color: ${({ theme, disabled }) =>
      disabled ? theme.colors.grey[400] : theme.colors.secondary};
    box-shadow: ${({ disabled }) =>
      disabled
        ? '0 1px 3px rgba(0, 0, 0, 0.08)'
        : '0 4px 12px rgba(0, 0, 0, 0.1)'};
    transform: ${({ disabled }) => (disabled ? 'none' : 'translateY(-2px)')};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
      opacity: 0.5;
      color: ${({ theme }) => theme.colors.grey[100]};

      & div,
      & span {
        color: ${({ theme }) => theme.colors.grey[300]};
      }
    `}

  @media (max-width: 768px) {
    height: 58px;
    padding: 8px 10px;
  }
`

const FolderName = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.haiti};
  margin-left: 8px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;

  @media (max-width: 768px) {
    font-size: 11px;
    margin-left: 6px;
    max-width: 100px;
  }
`

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const HeaderTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  margin-top: 16px;
`
const StyledFolderIcon = styled(FolderOpenIcon)`
  color: ${props => props.itemColor || props.theme.colors.grey[600]};
  transition: color 0.2s ease;
`
const SectionHeader = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.haiti};
  letter-spacing: 0.3px;
  background: none;
  border: none;
  padding: 0;
  text-align: left;

  ${({ open, $isClickable }) =>
    (open || $isClickable) &&
    css`
      cursor: pointer;
      transition: color 0.2s ease;

      &:hover {
        color: ${({ theme }) => theme.colors.primary};
      }

      @media (max-width: 768px) {
        &:active {
          opacity: 0.7;
        }
      }
    `}
`

const BreadcrumbsSign = styled(ChevronRightIcon)`
  color: ${({ theme }) => theme.colors.grey[600]};
  font-size: 18px !important;
  margin: 0 5px;
`

const CurrentFolderName = styled(SectionHeader)`
  color: ${({ theme }) => theme.colors.primary};
`

const SearchResultsCount = styled.span`
  margin-left: 8px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 14px;
`

const FilesCountBadge = styled.span`
  margin-left: 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.secondary};
  padding: 4px 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark || theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`

const FilesCounter = styled.span`
  display: inline-block;
  font-weight: 600;
  margin-left: 2px;
  color: ${({ theme }) => theme.colors.secondary || '#33becc'};
  font-size: 10px;
  background: ${({ theme }) => theme.colors.secondary || '#33becc'}10;
  padding: 2px 4px;
  border-radius: 3px;
`

const FolderNameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`

// Add the view toggle styling from OtherFiles
const ViewToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  background-color: ${({ theme }) => theme.colors.backgroundColor || '#f7f9fc'};
  border: 1px solid ${({ theme }) => theme.colors.grey?.[400] || '#e3e9f0'};
  border-radius: 12px;
  padding: 6px 8px;

  @media (max-width: 768px) {
    gap: 6px;
    padding: 4px 6px;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
`

const StyledIconButton = styled(IconButton)`
  color: ${({ isActive, theme }) =>
    isActive ? theme.colors.secondary : 'inherit'} !important;
  padding: 8px !important;
  min-width: 36px !important;
  min-height: 36px !important;
  width: 36px !important;
  height: 36px !important;

  @media (max-width: 768px) {
    padding: 6px !important;
    min-width: 36px !important;
    min-height: 36px !important;
    width: 36px !important;
    height: 36px !important;
  }

  svg {
    @media (max-width: 768px) {
      font-size: 18px !important;
    }
  }

  &:hover {
    color: ${props => props.theme.colors.secondary};
  }
`

const BulkActions = styled.div`
  flex: 1 1 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 6px;
  border-top: 1px solid ${({ theme }) => theme.colors.grey?.[400] || '#e3e9f0'};

  @media (max-width: 920px) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`

const ViewModeToggles = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    display: ${({ $hideOnMobile }) => ($hideOnMobile ? 'none' : 'flex')};
  }
`

const ChipClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  padding: 2px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  cursor: pointer;

  svg {
    font-size: 16px;
  }
`

const BulkActionsSpacer = styled.span`
  flex: 1 1 auto;
`

const BulkActionButton = styled.button`
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 34px;
  padding: 0 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  background: white;
  color: ${({ $danger, theme }) => ($danger ? '#b00020' : theme.colors.secondary || '#33becc')};
  border: 1px solid ${({ $danger, theme }) => ($danger ? '#b0002033' : theme.colors.grey?.[400] || '#dbe5ef')};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: background-color 0.2s ease;

  svg {
    font-size: 18px;
  }

  &:hover:not(:disabled) {
    background-color: ${({ $danger, theme }) => ($danger ? '#fdecea' : `${theme.colors.secondary || '#33becc'}10`)};
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }

  @media (max-width: 920px) {
    justify-content: center;
    min-width: 0;
    padding: 0 6px;
    font-size: 12px;
    gap: 4px;
    overflow: hidden;
    text-overflow: ellipsis;

    svg {
      font-size: 16px;
      flex-shrink: 0;
    }
  }
`

const BulkBlockedHint = styled.span`
  max-width: 320px;
  font-size: 12px;
  color: #6b7280;

  @media (max-width: 920px) {
    grid-column: 1 / -1;
    max-width: none;
  }
`

const ActionIconButton = styled(IconButton)`
  color: ${({ theme }) => theme.colors.secondary || '#33becc'};
  border: 1px solid ${({ theme }) => theme.colors.grey?.[400] || '#dbe5ef'};
  background-color: white;
  border-radius: 10px;
  padding: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    padding: 8px;
    min-width: 36px;
    min-height: 36px;
  }

  svg {
    @media (max-width: 768px) {
      font-size: 18px !important;
    }
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary || '#33becc'}08;
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: translateY(0);
  }
`

const SelectedFilesControls = styled.div`
  display: flex;
  align-items: center;
  margin-right: 16px;
  gap: 8px;

  @media (max-width: 768px) {
    margin-right: 8px;
    gap: 6px;
  }
`

const SelectedFilesCount = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary || '#1b2b3c'};
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.colors.backgroundColor || '#f7f9fc'};
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.grey?.[400] || '#dbe5ef'};
  margin-right: 8px;
  margin-left: 6px;
  display: flex;
  align-items: center;
  box-shadow: none;

  @media (max-width: 768px) {
    padding: 6px 10px;
    min-width: 36px;
    min-height: 36px;
    font-size: 13px;
    justify-content: center;
    box-shadow: none;
  }
`

const SelectedText = styled.span`
  margin-left: 4px;
`

const ClearButton = styled.button`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary || '#1b2b3c'};
  background: ${({ theme }) => theme.colors.backgroundColor || '#f7f9fc'};
  border: 1px solid ${({ theme }) => theme.colors.grey?.[400] || '#dbe5ef'};
  cursor: pointer;
  padding: 8px 12px;
  margin-right: 16px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    padding: 6px;
    margin-right: 6px;
    min-width: 36px;
    min-height: 36px;
    justify-content: center;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => theme.colors.grey?.[400] || '#dbe5ef'};
    background-color: ${({ theme }) => theme.colors.backgroundColor || '#f7f9fc'};
  }

  svg {
    @media (max-width: 768px) {
      font-size: 18px !important;
    }
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary || '#D24724'};
    background-color: ${({ theme }) => theme.colors.secondary || '#33becc'}08;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);

    @media (max-width: 768px) {
      background-color: ${({ theme }) => theme.colors.secondary || '#33becc'}12;
    }
  }
`

const ClearButtonText = styled.span`
  @media (max-width: 768px) {
    display: none;
  }
`

const ClearButtonIcon = styled.span`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const SelectAllText = styled.span`
  white-space: nowrap;

  @media (max-width: ${COMPACT_TOOLBAR_BREAKPOINT}px) {
    font-size: 13px;
  }
`

const SortText = styled.span`
  white-space: nowrap;

  @media (max-width: ${COMPACT_TOOLBAR_BREAKPOINT}px) {
    font-size: 13px;
    letter-spacing: 0.2px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`

const SelectAllButton = styled.button`
  font-size: 14px;
  font-weight: 500;
  color: ${({ $isDeselect, theme }) =>
    $isDeselect
      ? theme.colors.primary || '#D24724'
      : theme.colors.secondary || '#33becc'};
  background: ${({ theme }) => theme.colors.backgroundColor || '#f7f9fc'};
  border: 1px solid ${({ theme }) => theme.colors.grey?.[400] || '#dbe5ef'};
  cursor: pointer;
  padding: 8px 12px;
  margin-right: 16px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 10px;
  box-shadow: none;
  min-width: 36px;
  min-height: 36px;
  height: 36px;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 8px 12px;
    margin-right: 8px;
    min-height: 36px;
    min-width: 84px;
    width: auto;
    height: 36px;
    justify-content: center;
    box-shadow: none;
  }

  &:hover {
    color: ${({ $isDeselect, theme }) =>
      $isDeselect
        ? theme.colors.primary || '#D24724'
        : theme.colors.secondary || '#33becc'};
    background-color: ${({ theme }) => theme.colors.secondary || '#33becc'}08;
    box-shadow: none;

    @media (max-width: 768px) {
      box-shadow: none;
    }
  }
`

const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grey?.[400] || '#dbe5ef'};
  padding: 4px 8px;
  margin-right: 10px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: 6px;
    font-size: 12px;
    gap: 0;
    margin-right: 6px;
    min-height: 36px;
    min-width: 36px;
    justify-content: center;
  }

  svg {
    @media (max-width: 768px) {
      font-size: 18px !important;
    }
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary}08;
    border-color: ${({ theme }) => theme.colors.secondary};
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
  }

  svg {
    font-size: 18px;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }
`
const FolderSkeletonItem = styled.div`
  box-sizing: border-box;
  height: 64px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    height: 58px;
    padding: 8px 10px;
  }
`
const VirtualizedFilesWrapper = styled.div`
  width: 100%;
  flex: 1;
  min-height: 400px;
  margin-top: 20px;
  overflow: hidden;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  @media (max-width: 768px) {
    min-height: 300px;
    margin-top: 12px;
    flex: 1 1 auto;
    height: auto;
    min-height: calc(100vh - 350px);
    max-height: none;
    overflow: hidden;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 0;
    width: 100%;
    max-width: 100%;
  }
`

const SelectionOverlay = styled.div`
  position: absolute;
  border: 2px solid ${({ theme }) => theme.colors.secondary || '#33becc'};
  background-color: ${({ theme }) =>
    (theme.colors.secondary || '#33becc') + '25'};
  pointer-events: none;
  z-index: 1000;
  border-radius: 2px;
`

const DragSelectionHint = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 5px 9px;
  border: 1px solid ${({ theme }) => `${theme.colors.secondary || '#33becc'}40`};
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.9);
  color: ${({ theme }) => theme.colors.secondary || '#33becc'};
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(9, 21, 45, 0.08);
  backdrop-filter: blur(2px);
  pointer-events: none;
  user-select: none;
`

const LoadingFileCard = styled.div`
  height: 180px;
  margin: 8px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.backgroundColor};
  animation: pulse 1.5s infinite ease-in-out;

  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    50% {
      opacity: 0.8;
    }
    100% {
      opacity: 0.6;
    }
  }
`

const LoadingListItem = styled.div`
  height: 22px;
  margin: 1px 0;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.backgroundColor};
  animation: pulse 1.5s infinite ease-in-out;
`

const FileCountInfo = styled.div`
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin-top: 10px;
  margin-bottom: 20px;
`
const EmptyFolderDisplay = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 30px;
  margin: 40px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 2px dashed ${({ theme }) => theme.colors.lightGrey};
  border-radius: 16px;
  text-align: center;
  min-height: 300px;
`

const EmptyFolderIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.7;
  filter: grayscale(20%);
`

const EmptyFolderTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 8px 0;
`

const EmptyFolderMessage = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey[300]};
  margin: 0 0 24px 0;
  max-width: 300px;
  line-height: 1.4;
`

const EmptyFolderAction = styled.div`
  margin-top: 8px;
`

const AddButtonContainer = styled.div`
  grid-column: 1 / -1; // Span all columns if needed
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding: 0 8px; // Align with FoldersList padding
`

const ViewSwitchButton = styled(Button)`
  margin-left: 16px;
  height: 36px;
  text-transform: none !important;
  color: ${({ theme }) => theme.colors.primary} !important;
`
const TabLabelContent = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;
`

const TabText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  text-align: center;
`
const GridItemContainer = styled.div`
  position: relative;
  z-index: 1;
  isolation: isolate;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const UncategorisedFilterButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  padding: 6px 12px;
  margin-left: 16px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`

const ClearFilterButton = styled.button`
  background-color: ${({ theme }) => theme.colors.error || '#dc3545'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  padding: 6px 12px;
  margin-left: 16px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${({ theme }) => theme.colors.errorDark || '#c82333'};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.error || '#dc3545'};
    outline-offset: 2px;
  }
`

// List view header styled components
const GroupedList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  width: 100%;
`

const GroupSection = styled.div`
  & + & { margin-top: 2px; }
`

const GroupHeader = styled.button`
  position: sticky;
  top: 0;
  z-index: 2;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #eef0f2;
  background: #f4f6f8;
  color: #334155;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  text-align: left;

  &:hover { background: #e9edf2; }
  svg { color: #94a3b8; flex-shrink: 0; }
`

const GroupSwatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
`

const GroupName = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const GroupCount = styled.span`
  flex-shrink: 0;
  min-width: 22px;
  text-align: center;
  padding: 1px 8px;
  border-radius: 999px;
  background: #e3e8ee;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
`

const GroupEmpty = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
`

const ShowAllRow = styled.button`
  display: block;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: #f8fafc;
  color: #33becc;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;

  &:hover { background: #eef2f7; }
`

const ListViewHeader = styled.div`
  display: flex;
  align-items: center;
  height: 28px;
  background-color: #f8f9fa;
  border-bottom: 2px solid #e5e5e7;
  border-top: 1px solid #e5e5e7;
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: none;
  }
`

const HeaderCombined = styled.div`
  display: flex;
  align-items: center;
  padding: 0 12px 0 72px;
  flex: 2;
  min-width: 200px;
  border-right: 1px solid #e5e5e7;

  @media (max-width: 768px) {
    padding: 0 8px 0 48px;
    min-width: 150px;
    flex: 2.5;
  }
`

const HeaderFileType = styled.div`
  display: flex;
  align-items: center;
  padding: 0 12px;
  flex: 1.5;
  min-width: 150px;
  max-width: 300px;
  border-right: 1px solid #e5e5e7;

  @media (max-width: 768px) {
    padding: 0 8px;
    min-width: 100px;
    max-width: 150px;
    flex: 1.2;
  }
`

const HeaderFileDate = styled.div`
  display: flex;
  align-items: center;
  padding: 0 12px;
  flex: 1;
  min-width: 60px;
  max-width: 70px;
  border-right: 1px solid #e5e5e7;

  @media (max-width: 768px) {
    padding: 0 6px;
    min-width: 50px;
    max-width: 60px;
    flex: 0.8;
  }
`

const HeaderUploader = styled.div`
  display: flex;
  align-items: center;
  padding: 0 12px;
  flex: 1.5;
  min-width: 120px;
  max-width: 200px;
  border-right: 1px solid #e5e5e7;

  @media (max-width: 768px) {
    padding: 0 6px;
    min-width: 80px;
    max-width: 100px;
    flex: 1;
    display: none;
  }
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 60px;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  flex-direction: column;
  gap: 20px;
`

const AnimatedIconContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`

const BounceIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: bounce 1.5s infinite ease-in-out;
  animation-delay: ${props => props.delay || '0s'};

  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }
`

const LoadingText = styled.div`
  font-size: 16px;
  color: #666;
  font-weight: 500;
`

const ProgressContainer = styled.div`
  width: 200px;
  height: 4px;
  background-color: #e0e0e0;
  border-radius: 2px;
  overflow: hidden;
  position: relative;
`

const ProgressBar = styled.div`
  width: 40%;
  height: 100%;
  background-color: #33becc;
  border-radius: 2px;
  animation: slide 2s infinite ease-in-out;

  @keyframes slide {
    0% {
      transform: translateX(-100%);
    }
    50% {
      transform: translateX(250%);
    }
    100% {
      transform: translateX(-100%);
    }
  }
`

const CategoryLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  flex-direction: column;
  gap: 24px;
`

const FloatingIconsContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 20px;
  height: 50px;
`

const FloatingIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: float 3s infinite ease-in-out;
  animation-delay: ${props => props.delay || '0s'};

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px) rotate(0deg);
    }
    25% {
      transform: translateY(-15px) rotate(-5deg);
    }
    50% {
      transform: translateY(0px) rotate(5deg);
    }
    75% {
      transform: translateY(-8px) rotate(-2deg);
    }
  }
`

const PulsingText = styled.div`
  font-size: 18px;
  color: #666;
  font-weight: 600;
  animation: pulse 2s infinite ease-in-out;

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.6;
      transform: scale(1);
    }
    50% {
      opacity: 1;
      transform: scale(1.05);
    }
  }
`

const LoadingDots = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`

const Dot = styled.div`
  font-size: 16px;
  color: #33becc;
  animation: dotPulse 1.5s infinite ease-in-out;
  animation-delay: ${props => props.delay || '0s'};

  @keyframes dotPulse {
    0%,
    80%,
    100% {
      opacity: 0.3;
      transform: scale(0.8);
    }
    40% {
      opacity: 1;
      transform: scale(1.2);
    }
  }
`
const BreadcrumbLine = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
`

const BreadcrumbSmall = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`

const SearchLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.error};
  text-transform: uppercase;
`

const ResultsSummary = styled.div`
  font-size: 13px;
  color: #555;
  margin-top: 6px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`

const FolderResultText = styled.span`
  color: #333;
`

const ShowAllLink = styled.span`
  color: ${({ theme }) => theme.colors.secondary || '#33becc'};
  cursor: pointer;
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    text-decoration: none;
    opacity: 0.8;
  }
`

const FolderAccent = styled.span`
  color: ${({ theme }) => theme.colors.primaryDark || theme.colors.primary};
  font-weight: 700;
`

const SearchStatusBanner = styled.div`
  @keyframes searchSlideIn {
    from { opacity: 0; transform: translateY(-6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  animation: searchSlideIn 0.25s ease-out;
  margin: 0 0 12px 0;
  border-radius: 10px;
  background: ${({ $empty }) => $empty ? '#fdf6f4' : '#f4faf4'};
  border: 1px solid ${({ $empty }) => $empty ? '#f0ddd7' : '#d4ead4'};

  @media (max-width: 768px) {
    margin: 0 0 8px 0;
    border-radius: 8px;
  }
`

const SearchStatusContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;

  @media (max-width: 768px) {
    padding: 8px 12px;
    gap: 8px;
  }
`

const SearchStatusText = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ $empty }) => $empty ? '#9e3a1e' : '#374151'};
  letter-spacing: 0.01em;

  strong {
    font-weight: 700;
  }

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const SearchStatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $success, $empty }) =>
    $success ? '#4caf50' :
    $empty ? '#d24724' :
    '#33becc'};
`
